import type { Transaction } from "@tiptap/pm/state";
import { findReportSection } from "@/components/tiptap/impressionFreshness";
import { useParams } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getDefaultStore, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";

// Read the global focused-chip atom synchronously from the document-level
// keydown handler — useRef-mirroring is redundant since getDefaultStore().get
// is sync.
const jotaiStore = getDefaultStore();

import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useHotkeys } from "react-hotkeys-hook";
import "./ReporterStyles.css";
import "./ReporterJobs.css";

import { DictationProvider, useDictationActions } from "./case-viewer/DictationProvider";
import { useToken, useUserId } from "../context/AuthContext";
import { useReport, useReportLayout } from "../hooks/useReportQuery";
import { useEffectiveTemplate, getTemplateCharCount } from "../hooks/useRadTemplate";
import type { ReportTemplate } from "../hooks/useTemplate";
import {
  buildDictationContent,
  buildImpressionSection,
  buildReportDoc,
  defaultAttributeSelection,
  gateEditAttrsForChip,
  gateMeasureForChip,
  getReportText,
  humanizeBaseFromCondition,
  isStaleDraft,
  extractFindingsForImpression as extractFindingsFromReportDoc,
  hasLegacyTemplateImpression,
  isUnworkedTemplateDraft,
  joinPhrasingWithMeasurement,
  phrasingDefaultSelection,
  rebuildPhrase,
  resolveReportExamType,
  spliceAtCaret,
  templateForReportSections,
  type ChipGateWarnId,
} from "../utils/reportUtils";
import {
  streamImpression as requestImpression,
  type ImpressionItem,
} from "../utils/impressionStream";
import type { FindingJumpTarget } from "../hooks/useReportQuery";
import { ChipContextMenu, type ChipAction } from "./reporter/ChipContextMenu";
import { useSectionContextMenu } from "./reporter/useSectionContextMenu";
import { ImpressionContextMenu } from "./reporter/ImpressionContextMenu";
import {
  type ReportFocus,
  reportFocusAtom,
  getFocusedChipId,
  inlineCardOpenChipIdAtom,
  dictationGhostChipAtom,
  dictationGhostStripAtom,
} from "../atoms/reporterChipAtoms";
import { logger } from "../utils/logger";
import { stoneOverlayMeasurementAtom } from "../atoms/stoneOverlayAtoms";
import { resolveSectionKindAt } from "./tiptap/extensions";
import { OriginalReportPanel } from "./case-viewer/OriginalReportPanel";
import { ReportControlBar, type DictationPhase } from "./case-viewer/ReportControlBar";
import { MicTestPanel } from "./case-viewer/MicTestPanel";
import { InlineChipCard } from "./reporter/InlineChipCard";
import { micTestOpenAtom } from "../atoms/dictationModalAtoms";
import { measureModeAtom, measureResultAtom, measureLoadingAtom } from "../atoms/measureAtoms";
import { postprocessDictation, voiceAdjust } from "../utils/openaiUtils";
import { hoveredSurfaceAtom } from "../atoms/hoveredSurfaceAtom";
import { emit } from "../lib/viewerBus";
import { runCommand } from "../lib/commands";
import { useReportState } from "../hooks/useStudyQueries";
import { useSaveReportState, useSignReport, useUnsignReport } from "../hooks/useReporterMutations";
import type { ReaderReportContext } from "../hooks/useStudyEventLog";
import { createReportSaveQueue } from "../utils/reportSaveQueue";
import type { ReportStateResponse } from "../hooks/useStudyQueries";
import type { StructuredReportResponse } from "../hooks/useReportQuery";
import { autoSaveEnabledAtom, autoSaveStatusAtom } from "../atoms/reporterAtoms";
import {
  dictationSessionAtom,
  dictationStageAtom,
  dictationErrorAtom,
  dictationDeviceAtom,
  dictationLiveTranscriptAtom,
  appendDictationHistory,
  type DictationMode,
} from "../atoms/dictationSessionAtoms";
import DragHandle from "@tiptap/extension-drag-handle";
import {
  AddImpressionItemExtension,
  FindingToImpressionDropExtension,
  AddOrganFindingExtension,
  RegenerateImpressionExtension,
  regenerateImpressionRefreshMeta,
  ImpressionParagraphAttrs,
  DictationGhostExtension,
  ReportStructureGuard,
  EmptyImpressionItemGC,
  dictationGhostPluginKey,
  FieldNavigation,
  findAllFields,
  findFieldNodeById,
  insertRadFinding,
} from "./tiptap/extensions";
import {
  FindingsSectionNode,
  ImpressionSectionNode,
  OrganSubsectionNode,
  ImpressionItemNode,
} from "./tiptap/nodes";
import { CHIP_INNER_CLASS, FieldNode } from "./tiptap/marks";
import { VerticalArrowNav } from "./tiptap/verticalNav";
import { attachBulkFieldDeleteToast, BULK_DELETE_TOAST_SKIP_META } from "./tiptap/bulkDeleteToast";
import { SuggestionStrip } from "./reporter/SuggestionStrip";
import { StripSplit } from "./reporter/StripSplit";
import { useUserPrefs } from "../hooks/useUserPrefs";
import {
  focusedStripRowAtom,
  showLowConfidenceFindingsAtom,
  showNormalFieldsAtom,
  type ReporterAiMode,
} from "../atoms/reporterPrefsAtoms";
import { userTypeAtom } from "../atoms/authAtoms";
import { Ghost } from "./common/Ghost";
import { isInternalRole } from "../services/roles";
import { reviewerVerdictsAtom } from "../atoms/reviewerPrefsAtoms";
import { useReviewerFindingVerdicts } from "../hooks/useReviewerFindingVerdicts";
import { ReportTemplateGate } from "./reporter/ReportTemplateGate";
import { isWordingReady, requireReadyWording } from "../utils/reportWording";

// Report panel cold-paint placeholder. Shaped like the report's section header
// + lines — no spinner. (loading-no-phase-changes)
const ReportLoadingGhost: React.FC = () => (
  <div className="report-container">
    <div className="p-4 space-y-3">
      <p role="status" className="text-sm text-fg-muted">
        Preparing draft…
      </p>
      <Ghost variant="card" className="h-4 w-2/5 border-0! bg-surface-2" />
      <Ghost variant="card" className="h-3 w-full border-0! bg-surface" />
      <Ghost variant="card" className="h-3 w-11/12 border-0! bg-surface" />
      <Ghost variant="card" className="h-3 w-4/5 border-0! bg-surface" />
    </div>
  </div>
);

interface PrototypeReportProps {
  readerContext?: ReaderReportContext;
  studyId?: string;
  onFindingClick?: (target: FindingJumpTarget) => void;
  sessionMode: "demo" | "labeling-reporting";
  templateMode: "ai" | "blank";
  /** Commits the reader-session final snapshot and event; rejects on failure. */
  onSigned?: (ctx: StudySignContext) => Promise<void> | void;
  /** Fires once per `studyId` when the editor's initial doc has settled
   *  (template loaded, AI folded in if applicable). Reader-study wiring
   *  captures the baseline snapshot here. Doesn't fire for already-signed
   *  reports. */
  onInitialDoc?: (ctx: StudyInitialDocContext) => void;
  /** Optional sign confirmation gate. When set, replaces the default
   *  `window.confirm()` prompt — the sign mutation only fires after the
   *  promise resolves with a non-null value. Resolve null to cancel. */
  confirmSign?: () => Promise<StudySignConfirmation>;
  /** Hide the OriginalReportPanel at the bottom of the panel. The
   *  labeling/reporting flow doesn't want it in view. */
  hideOriginalReport?: boolean;
  /** Reviewer verdict mode: render the reporter inline UX for a REVIEWER, wiring
   *  right-click chip verdicts to the cohort/study and disabling dictation +
   *  the reporter control bar (the ReviewerControlBar owns the bottom bar). */
  reviewVerdict?: { cohortId: string; studyUid: string };
  /** Task 9: surface the Tiptap editor to a parent (CaseViewer) so the renal-
   *  stone review surface can fold approved stones into the report. Called with
   *  the editor when it mounts and with `null` when it unmounts, so the parent
   *  never syncs against a destroyed editor. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Fires each time report content is (re)applied to the editor via
   *  setContent (initial build, findings recovery, reset draft). The parent
   *  bumps a version so useStoneReportSync re-runs and re-injects the stone
   *  chip that setContent wiped. */
  onReportContentApplied?: () => void;
}

export interface StudySignContext {
  activeReadingMs?: number;
  expectedRevision?: string;
  templateId: string;
  templateCharCount: number;
  difficulty?: number;
}

export interface StudyInitialDocContext {
  reportHtml: string;
  templateId: string;
  templateCharCount: number;
}

export type StudySignConfirmation = { difficulty: number; activeReadingMs?: number } | null;

type ImpressionStatus = "idle" | "streaming" | "done" | "error";

export function jumpTargetFromChipDom(el: Element): FindingJumpTarget | null {
  if (!el.classList.contains("has-link")) return null;
  const sid = el.getAttribute("data-series-id");
  const idxRaw = el.getAttribute("data-slice-index");
  if (!sid || idxRaw === null) return null;
  const sliceIndex = Number.parseInt(idxRaw, 10);
  if (Number.isNaN(sliceIndex)) return null;
  return {
    seriesId: sid,
    sliceIndex,
    frameOfReferenceUid: el.getAttribute("data-for-uid"),
  };
}

// User-facing copy for chip entry-point refusals, keyed by the gate's warn
// id. Typed Record<ChipGateWarnId, string> so a new refusal reason can't
// ship without copy.
const CHIP_GATE_TOASTS: Record<ChipGateWarnId, string> = {
  chip_edit_attrs_unavailable: "This finding has no editable attributes.",
  measure_chip_not_found: "Measurement isn't available for this finding.",
  measure_no_phrase_sources: "Measurement isn't available for this finding.",
  measure_no_condition: "Measurement isn't available for this finding.",
};

/** Strip-mode plain dictation target — the focused SuggestionStrip card.
 *  Set by the keydown handler at gesture-start, consumed once at finalize.
 *  `caretOffset` snapshots the input's selectionStart so literal mode can
 *  splice at the same column the rad was editing in. */
interface StripDictationTarget {
  rowId: string;
  originalText: string;
  apply: (next: string) => void;
  caretOffset: number | null;
}

export default function PrototypeReport({
  readerContext,
  studyId: propStudyId,
  onFindingClick,
  sessionMode,
  templateMode,
  onSigned,
  onInitialDoc,
  confirmSign,
  hideOriginalReport,
  reviewVerdict,
  onEditorReady,
  onReportContentApplied,
}: PrototypeReportProps) {
  const { "*": urlStudyId } = useParams();
  const studyId = propStudyId || urlStudyId;
  const token = useToken();
  const userId = useUserId();

  // Bridge: the provider's stable onFinalTranscript callback delegates to
  // PrototypeReportInner's dispatchTranscript via this ref, which Inner sets
  // on every render. This keeps the provider outside Inner so the WS lifetime
  // is not tied to Inner's re-mounts.
  const dispatchTranscriptRef = useRef<((text: string) => Promise<void>) | null>(null);
  const onFinalTranscript = useCallback(async (text: string) => {
    if (!dispatchTranscriptRef.current) throw new Error("Report editor is unavailable.");
    await dispatchTranscriptRef.current(text);
  }, []);

  // Demo mode renders an ephemeral session — skip the saved-state read so each
  // refresh starts fresh. Labeling-reporting mode persists across sessions.
  const persistenceEnabled = sessionMode !== "demo" && !readerContext?.isPractice;

  // Reporter A/B mode lives in `a2z-medi-flow` per-user (see useUserPrefs).
  // `mode === "off"` is the baseline-measurement path — short-circuit the
  // AI fetch by extending the existing template-mode gate. Hold the inner
  // render until prefs hydrate; mounting with the atom default and then
  // flipping to the persisted value would trip the inner's mode-change
  // auto-clear effect and wipe a saved draft on every page load.
  const {
    mode: reporterAiMode,
    setMode: setReporterAiMode,
    isLoading: isPrefsLoading,
  } = useUserPrefs();
  const aiTemplateEnabled = templateMode !== "blank" && reporterAiMode !== "off";

  const {
    data: savedReportState,
    isPending: isReportStatePending,
    isFetching: isReportStateFetching,
    isSuccess: isReportStateSuccess,
    error: reportStateError,
    refetch: refetchReportState,
  } = useReportState(token, userId ?? null, studyId ?? null, {
    enabled: persistenceEnabled,
    readerScheduleId: readerContext?.scheduleId,
  });

  // Disabled queries still expose shared cached data. Ephemeral sessions must
  // ignore it; persistent sessions must finish the authoritative read first.
  const reportState = persistenceEnabled ? savedReportState : undefined;
  const isReportStateLoading = persistenceEnabled && isReportStatePending;
  const isReportSigned = reportState?.is_report_signed ?? false;
  const reportStateReady =
    !persistenceEnabled || (isReportStateSuccess && !isReportStateFetching && !!savedReportState);

  // Preparing a fresh report and loading an existing report are different
  // backend operations, so `useReport` includes that choice in its query key.
  // Freeze the choice once per report session: the first autosave changes the
  // saved state from null to a document, but it must not switch query identity
  // and unmount the editor while a newer edit is still waiting to be saved.
  const reportSessionIdentity = JSON.stringify([
    userId,
    studyId,
    sessionMode,
    readerContext?.scheduleId,
    readerContext?.isPractice,
  ]);
  const initialReportPreparation = useRef<{
    sessionIdentity: string;
    prepare: boolean;
  } | null>(null);
  if (
    reportStateReady &&
    initialReportPreparation.current?.sessionIdentity !== reportSessionIdentity
  ) {
    initialReportPreparation.current = {
      sessionIdentity: reportSessionIdentity,
      prepare: !reportState?.structured_editor_state,
    };
  }
  const prepareReport =
    initialReportPreparation.current?.sessionIdentity === reportSessionIdentity
      ? initialReportPreparation.current.prepare
      : !reportState?.structured_editor_state;

  // Skip the LLM-phrasing call entirely for signed reports — the saved editor
  // state is sufficient to render and all actions that need LLM data are
  // disabled when signed. Also skip when the per-study template_mode is
  // "blank" so the editor opens empty for radiologists who bring their own
  // template.
  const {
    data: reportData,
    isLoading,
    error,
  } = useReport(studyId ?? null, {
    enabled: reportStateReady && !isReportSigned && aiTemplateEnabled,
    prepare: prepareReport,
  });
  // Blank-template and AI-off modes still need the same study-to-template
  // decision as AI drafting. This lightweight path runs prediction/coverage
  // selection but skips clinical-language generation entirely.
  const layoutRequired = !isReportSigned && !aiTemplateEnabled;
  const {
    data: reportLayout,
    isLoading: isReportLayoutLoading,
    error: reportLayoutError,
  } = useReportLayout(studyId ?? null, {
    enabled: reportStateReady && layoutRequired,
  });
  const reportExamType = resolveReportExamType(
    reportData?.meta.examType,
    reportLayout?.meta.examType,
    // An unsigned draft must be checked against the current backend registry
    // decision. Its saved identity is evidence about the old draft, not a
    // substitute for current coverage/model output. Signed reports are
    // immutable and may render from their persisted identity alone.
    isReportSigned ? reportState?.report_exam_type : null,
  );
  const {
    data: effectiveTemplate,
    isLoading: isEffectiveTemplateLoading,
    error: effectiveTemplateError,
  } = useEffectiveTemplate(reportExamType);

  if (!studyId) {
    return (
      <div className="report-container">
        <div className="report-empty">Select a study to view the report.</div>
      </div>
    );
  }

  const reportSourceLoading =
    !isReportSigned && (aiTemplateEnabled ? isLoading : isReportLayoutLoading);
  if (isReportStateLoading || isPrefsLoading) {
    return <ReportLoadingGhost />;
  }

  if (persistenceEnabled && !savedReportState)
    return <div role="alert">The report could not be loaded. Reload to try again.</div>;
  if (reportSourceLoading) return <ReportLoadingGhost />;
  const reportSourceError = aiTemplateEnabled ? error : reportLayoutError;
  if (!isReportSigned && reportSourceError) {
    return (
      <div className="report-container">
        <div className="report-error">
          <span className="text-warn text-sm">{(reportSourceError as Error).message}</span>
        </div>
      </div>
    );
  }

  if (!isReportSigned && aiTemplateEnabled && !reportData) {
    return (
      <div className="report-container">
        <div className="report-empty">Report unavailable.</div>
      </div>
    );
  }

  if (
    !isReportSigned &&
    aiTemplateEnabled &&
    !isWordingReady(reportData?.meta.wording) &&
    isStaleDraft(reportState?.structured_editor_state)
  ) {
    return (
      <div role="alert" className="report-container">
        The saved report uses an older format. A new AI draft is unavailable, so the saved report
        has not been changed.
      </div>
    );
  }

  if (!isReportSigned && layoutRequired && !reportLayout) {
    return (
      <div className="report-container">
        <div className="report-empty">Report layout unavailable.</div>
      </div>
    );
  }

  return (
    <>
      {persistenceEnabled && reportStateError && (
        <p role="alert" className="px-4 text-sm text-fg-muted">
          Could not refresh report status.{" "}
          <button type="button" className="underline" onClick={() => void refetchReportState()}>
            Retry
          </button>
        </p>
      )}
      <ReportTemplateGate
        isSigned={isReportSigned}
        examType={reportExamType}
        template={effectiveTemplate}
        isLoading={isEffectiveTemplateLoading}
        error={effectiveTemplateError}
        savedEditorState={reportState?.structured_editor_state}
        savedExamType={reportState?.report_exam_type}
        loadingFallback={<ReportLoadingGhost />}
        errorFallback={(templateError) => (
          <div className="report-container">
            <div className="report-error">
              <span className="text-warn text-sm">{(templateError as Error).message}</span>
            </div>
          </div>
        )}
        unavailableFallback={
          <div className="report-container">
            <div className="report-empty">Report template unavailable.</div>
          </div>
        }
      >
        {({ template: resolvedTemplate, templateMismatchDraftDetected }) => (
          <DictationProvider userId={userId} token={token} onFinalTranscript={onFinalTranscript}>
            <PrototypeReportInner
              key={JSON.stringify([
                userId,
                studyId,
                readerContext?.scheduleId,
                readerContext?.isPractice,
              ])}
              readerContext={readerContext}
              studyId={studyId}
              userId={userId}
              token={token}
              reportData={reportData ?? null}
              reportExamType={reportExamType}
              effectiveTemplate={resolvedTemplate}
              templateMismatchDraftDetected={templateMismatchDraftDetected}
              reportState={reportState ?? null}
              onFindingClick={onFindingClick}
              sessionMode={sessionMode}
              aiTemplateEnabled={aiTemplateEnabled}
              reporterAiMode={reporterAiMode}
              setReporterAiMode={setReporterAiMode}
              dispatchTranscriptRef={dispatchTranscriptRef}
              onSigned={onSigned}
              onInitialDoc={onInitialDoc}
              confirmSign={confirmSign}
              hideOriginalReport={hideOriginalReport}
              reviewVerdict={reviewVerdict}
              onEditorReady={onEditorReady}
              onReportContentApplied={onReportContentApplied}
            />
            <MicTestPanel />
          </DictationProvider>
        )}
      </ReportTemplateGate>
    </>
  );
}

interface PrototypeReportInnerProps {
  studyId: string;
  userId: string | null;
  token: string | null;
  reportData: StructuredReportResponse | null;
  /** Backend registry decision. Comes from /api/report for AI drafting and the
   * lightweight structured-findings path for blank/AI-off drafting. */
  reportExamType: string | null;
  /** Effective global or per-radiologist template, resolved before this
   * TipTap component is allowed to mount. */
  effectiveTemplate: ReportTemplate | undefined;
  /** Saved draft layout differs from the resolved current template. Computed
   * by the outer mount gate so the old draft never gets a transient mount. */
  templateMismatchDraftDetected: boolean;
  readerContext?: ReaderReportContext;
  reportState: Pick<
    ReportStateResponse,
    "is_report_signed" | "structured_editor_state" | "report_exam_type" | "report_revision"
  > | null;
  onFindingClick?: (target: FindingJumpTarget) => void;
  sessionMode: "demo" | "labeling-reporting";
  /** False when the project is configured for blank-template reporting OR
   *  when the rad has chosen the "off" A/B arm: no AI findings are fetched
   *  and the editor seeds from the negative template defaults only. */
  aiTemplateEnabled: boolean;
  reporterAiMode: ReporterAiMode;
  setReporterAiMode: (next: ReporterAiMode) => void;
  /** Bridge ref: set to dispatchTranscript on each render so the outer
   *  DictationProvider's onFinalTranscript callback always delegates to the
   *  current handler without re-mounting the provider. */
  dispatchTranscriptRef: React.RefObject<((text: string) => Promise<void>) | null>;
  /** Commits the reader-session final snapshot and event; rejects on failure. */
  onSigned?: (ctx: StudySignContext) => Promise<void> | void;
  /** Fires once per `studyId` after the editor's initial doc settles. */
  onInitialDoc?: (ctx: StudyInitialDocContext) => void;
  /** Optional sign-confirmation gate (replaces window.confirm). */
  confirmSign?: () => Promise<StudySignConfirmation>;
  /** Hide the OriginalReportPanel render. */
  hideOriginalReport?: boolean;
  /** Reviewer verdict mode: render the reporter inline UX for a REVIEWER, wiring
   *  right-click chip verdicts to the cohort/study and disabling dictation +
   *  the reporter control bar (the ReviewerControlBar owns the bottom bar). */
  reviewVerdict?: { cohortId: string; studyUid: string };
  /** Task 9: fires with the editor on mount, `null` on unmount. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Fires after each report-content setContent so the parent can trigger a
   *  stone-chip re-injection (setContent wipes the injected chip). */
  onReportContentApplied?: () => void;
}

function PrototypeReportInner({
  readerContext,
  studyId,
  userId,
  token,
  reportData,
  reportExamType,
  effectiveTemplate,
  templateMismatchDraftDetected,
  reportState,
  onFindingClick,
  sessionMode,
  aiTemplateEnabled,
  reporterAiMode,
  setReporterAiMode,
  dispatchTranscriptRef,
  onSigned,
  onInitialDoc,
  confirmSign,
  hideOriginalReport,
  reviewVerdict,
  onEditorReady,
  onReportContentApplied,
}: PrototypeReportInnerProps) {
  const onFindingClickRef = useRef(onFindingClick);
  onFindingClickRef.current = onFindingClick;

  const saveReportState = useSaveReportState(token);
  const signReport = useSignReport(token);
  const unsignReport = useUnsignReport(token);
  const [autoSaveEnabled] = useAtom(autoSaveEnabledAtom);
  const [autoSaveStatus, setAutoSaveStatus] = useAtom(autoSaveStatusAtom);
  const initialSigned = reportState?.is_report_signed ?? false;
  const [isSigned, setIsSigned] = useState(initialSigned);
  const pendingReaderSign = useRef<StudySignContext | null>(null);
  const readerSignInFlight = useRef(false);
  const [isReaderSigning, setIsReaderSigning] = useState(false);
  const [readerSaveQueue] = useState(() =>
    readerContext && !readerContext.isPractice && userId && reportState?.report_revision
      ? createReportSaveQueue(reportState.report_revision, (update) =>
          saveReportState.mutateAsync({
            userId,
            studyId,
            readerScheduleId: readerContext.scheduleId,
            ...update,
          }),
        )
      : null,
  );

  // Reviewer verdict mode: fetch this reviewer's per-finding verdicts for the
  // cohort/study and mirror them into reviewerVerdictsAtom so ChipView can
  // tint chips without prop drilling. Reset to {} on cleanup so reporter
  // chips never inherit stale tints from a previous review session.
  const setReviewerVerdicts = useSetAtom(reviewerVerdictsAtom);
  const { verdicts: reviewerVerdicts, setVerdict: setReviewerVerdict } = useReviewerFindingVerdicts(
    reviewVerdict ? token : null,
    reviewVerdict ? userId : null,
    reviewVerdict?.cohortId ?? null,
    reviewVerdict?.studyUid ?? null,
  );
  useEffect(() => {
    if (!reviewVerdict) return;
    setReviewerVerdicts(reviewerVerdicts);
    return () => setReviewerVerdicts({}); // reset so reporter chips never inherit tints
  }, [reviewVerdict, reviewerVerdicts, setReviewerVerdicts]);

  const [impressionStatus, setImpressionStatus] = useState<ImpressionStatus>("idle");
  const [showNormalFields, setShowNormalFields] = useAtom(showNormalFieldsAtom);
  // The InlineChipCard renders the attribute pills inline under the focused
  // chip. "Edit attributes" (the chip right-click menu, or the E key) focuses
  // the chip and opens the card.

  // Right-click context menu for chips — actions live in the menu, the chip
  // stays at rest visually so the report text never reflows. Identified by
  // chip id so the menu can survive HMR-driven DOM churn without going stale.
  const [chipContextMenu, setChipContextMenu] = useState<{
    chipId: string;
    anchor: { x: number; y: number };
    canEditAttrs: boolean;
    canMeasure: boolean;
    canDismiss: boolean;
    canAddFinding: boolean;
    canRegenerate: boolean;
    canVerdict: boolean;
    condition: string | null;
  } | null>(null);
  const closeChipContextMenu = useCallback(() => setChipContextMenu(null), []);

  // Right-click context menu for the IMPRESSION header / any impression item.
  // Offers "Regenerate impression" as a second path to the header ↻ button.
  const [impressionContextMenu, setImpressionContextMenu] = useState<{
    anchor: { x: number; y: number };
  } | null>(null);
  const closeImpressionContextMenu = useCallback(() => setImpressionContextMenu(null), []);

  // Report focus SSoT — one atom captures the rad's exact location in the
  // report (chip-focused / caret-in-chip / caret-in-prose / none). Outline
  // styling, InlineChipCard mount, dictation routing, and Tab navigation
  // all read this. See reporterChipAtoms.ts for mode semantics.
  const [reportFocus, setReportFocus] = useAtom(reportFocusAtom);
  // Derived: chipId is set when the rad is on a chip (either Tab-focused
  // with NodeSelection or caret clicked into the inner contentEditable).
  // Used as the styling/jump anchor; mode is what disambiguates for
  // dictation routing and keyboard shortcut gating.
  const focusedChipId = getFocusedChipId(reportFocus);

  // Click-to-measure (📐 button on cortex findings). PrototypeReport sets
  // measureModeAtom; CaseViewer's click listener fires nn-interactive and
  // writes measureResultAtom; the effect below applies the result to the
  // matching field.
  const [measureMode, setMeasureMode] = useAtom(measureModeAtom);
  const [measureResult, setMeasureResult] = useAtom(measureResultAtom);
  const [, setMeasureLoading] = useAtom(measureLoadingAtom);
  const setHoveredSurface = useSetAtom(hoveredSurfaceAtom);
  // Transient warnings (postprocess unavailable, dictation backend error,
  // impression generation failure, voice-adjust failure) fire on the
  // page-level toast bus (viewerBus `toast` channel). The PageToast host,
  // mounted once at the App layout, renders + auto-dismisses — so warnings
  // also show on the standalone /prototype-report page, not just the viewer.

  // Voice-adjust (ctrl+` push-to-talk over an AI finding chip).
  type VoiceAdjustState = "idle" | "recording" | "transcribing" | "adjusting";
  const [voiceAdjustState, setVoiceAdjustState] = useState<VoiceAdjustState>("idle");
  const voiceAdjustHeldRef = useRef(false);
  // Wallclock time the rad pressed ctrl+`, so the keyup handler can
  // detect short-tap releases (no audio captured) and surface a more
  // actionable toast than "No instruction heard."
  const voiceAdjustHoldStartRef = useRef<number>(0);
  // Both gestures share the speech pipeline. The mode is captured at
  // keydown time and read by the unified onFinalTranscript router.
  const dictationModeRef = useRef<DictationMode>("smart");
  const voiceAdjustTargetRef = useRef<{
    fieldStart: number;
    fieldEnd: number;
    originalText: string;
    /** Null when target is a plain selection (no field mark to preserve). */
    markAttrs: Record<string, unknown> | null;
  } | null>(null);

  // Strip-mode voice-adjust target. Set by the keydown handler when a strip
  // card is focused; cleared by handleVoiceAdjustTranscript. Mutually
  // exclusive with `voiceAdjustTargetRef` — strip target takes precedence.
  const stripTuneTargetRef = useRef<{
    rowId: string;
    originalText: string;
    apply: (next: string) => void;
  } | null>(null);

  // Set at ` / Shift+` keydown when a strip card is focused; consumed by
  // handleDictationResult. Takes precedence over the reportFocusAtom
  // snapshot — without it, smart and literal would route into whatever chip
  // the report focus atom last captured, defeating strip mode (the card IS
  // the editing surface).
  const stripDictationTargetRef = useRef<StripDictationTarget | null>(null);

  // One captured editor destination survives cleanup. Only prose positions
  // are mapped as the document changes; chip destinations use their stable id.
  type EditorDictationTarget = {
    focus: ReportFocus;
    pos: number | null;
    caretOffset: number | null;
  };
  const editorDictationTargetRef = useRef<EditorDictationTarget | null>(null);

  // Per-session A/B counters stamped onto the /sign_report write. Refs (not
  // state) so the keydown handler and signReport callback read the latest
  // value without re-creating their closures on every change. Strip mode is
  // the only arm with discrete Accept/Dismiss events; inline kept-by-default
  // and off has no AI — accept/dismiss are sent as undefined for non-strip
  // arms so the backend drops them (null = not-applicable, not zero).
  const acceptedFindingsCountRef = useRef(0);
  const dismissedFindingsCountRef = useRef(0);
  const voiceTuneCountRef = useRef(0);

  // Mirror the focused strip card synchronously so the global ` keydown
  // handler can route voice-adjust to the strip without rebinding on every
  // focus change. The Jotai atom is the SSoT; useAtomValue keeps the ref
  // updated.
  const focusedStripRow = useAtomValue(focusedStripRowAtom);
  const focusedStripRowRef = useRef(focusedStripRow);
  focusedStripRowRef.current = focusedStripRow;

  // Gear toggle (default off): hide review-tier "maybe" findings. Gates the
  // inline doc build below and the strip cards in SuggestionStrip.
  const showLowConfidenceFindings = useAtomValue(showLowConfidenceFindingsAtom);

  const userType = useAtomValue(userTypeAtom);
  const canEditTemplates = isInternalRole(userType) || userType === "reviewer";

  const isBacktickHeldRef = useRef(false);
  // `| null` matches useEditor's nullable return and the null initial value;
  // editorRef.current is reassigned after mount (React 19 refs are writable).
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null);

  const findFieldAtCursor = useCallback((ed: NonNullable<typeof editor>, pos: number) => {
    const fields = findAllFields(ed.state.doc);
    return fields.find((f) => pos >= f.start && pos <= f.end) || null;
  }, []);

  // Preview follows the captured destination until insertion or cancellation.
  const setGhostText = useCallback((text: string | null) => {
    const ed = editorRef.current;
    if (!text) {
      jotaiStore.set(dictationGhostChipAtom, null);
      jotaiStore.set(dictationGhostStripAtom, null);
      if (ed?.view && !ed.isDestroyed) {
        ed.view.dispatch(ed.state.tr.setMeta(dictationGhostPluginKey, null));
      }
      return;
    }
    if (!ed?.view || ed.isDestroyed) return;
    const stripTarget = stripDictationTargetRef.current;
    if (stripTarget) {
      jotaiStore.set(dictationGhostStripAtom, {
        rowId: stripTarget.rowId,
        text,
        offset: stripTarget.caretOffset,
      });
      return;
    }
    const target = editorDictationTargetRef.current;
    const chipId = target ? getFocusedChipId(target.focus) : null;
    if (target && chipId) {
      jotaiStore.set(dictationGhostChipAtom, { chipId, text, offset: target.caretOffset });
      ed.view.dispatch(ed.state.tr.setMeta(dictationGhostPluginKey, null));
      return;
    }
    const pos = target?.pos ?? ed.state.selection.from;
    ed.view.dispatch(ed.state.tr.setMeta(dictationGhostPluginKey, { pos, text }));
    jotaiStore.set(dictationGhostChipAtom, null);
  }, []);

  // Completion, cancellation, failed starts and delivery errors retire the
  // same gesture state. A microphone test must never inherit a report target.
  const resetDictationGesture = useCallback(() => {
    editorDictationTargetRef.current = null;
    stripDictationTargetRef.current = null;
    voiceAdjustTargetRef.current = null;
    stripTuneTargetRef.current = null;
    isBacktickHeldRef.current = false;
    voiceAdjustHeldRef.current = false;
    setVoiceAdjustState("idle");
    setGhostText(null);
  }, [setGhostText]);

  const showPostprocessWarning = useCallback((message: string) => emit("toast", { message }), []);

  const actions = useDictationActions();
  const setMicTestOpen = useSetAtom(micTestOpenAtom);
  const stage = useAtomValue(dictationStageAtom);
  const dictationHookError = useAtomValue(dictationErrorAtom);
  const device = useAtomValue(dictationDeviceAtom);
  const liveTranscript = useAtomValue(dictationLiveTranscriptAtom);
  const store = useStore();

  // Derived state (keeps existing variable names so no downstream renames).
  const isConnecting = stage === "connecting";
  const isDictating = stage === "listening";
  const isProcessing = stage === "finalizing" || stage === "delivering";
  const isDictationReady = stage === "ready";
  const audioQuality = device.quality;

  // Single dictation-status signal for the control-bar Dictate button (the
  // status home — the old fixed overlay is gone). Voice-adjust shares the
  // dictation stage atom, so check voiceAdjustState FIRST: otherwise a ctrl+`
  // refine (stage "listening") would read as editor "Listening…". Strip-row
  // dictation is a first-class editor-mic path, so it falls through to
  // "listening" like editor dictation.
  const dictationPhase: DictationPhase =
    voiceAdjustState === "recording"
      ? "refining"
      : voiceAdjustState === "transcribing"
        ? "transcribing"
        : voiceAdjustState === "adjusting"
          ? "adjusting"
          : isDictating
            ? "listening"
            : isProcessing
              ? "transcribing"
              : isConnecting
                ? "connecting"
                : "idle";

  // Action wrappers so existing call sites are unchanged.
  const connect = actions.connect;
  const startDictation = actions.startDictation;
  const stopDictation = actions.stopDictation;
  const cancelDictation = actions.cancelDictation;

  // Strip-mode plain dictation — the focused SuggestionStrip card is the
  // editing surface, not the editor doc.
  const applyStripDictation = useCallback(
    async (stripTarget: StripDictationTarget, transcript: string) => {
      const mode = dictationModeRef.current;
      if (!transcript.trim()) {
        appendDictationHistory(store, {
          ts: Date.now(),
          text: transcript,
          postprocessed: "",
          mode: store.get(dictationSessionAtom).mode,
          dispatch: { kind: "dropped", reason: "empty" },
        });
        return;
      }
      // Post-process both modes (capitalize, mm/cm abbreviation) — matches
      // the chip dictation contract; smart/literal differ only in apply
      // semantics. On post-process failure, insert raw transcript so the
      // rad's words aren't lost.
      let textToApply = transcript;
      if (userId && token) {
        try {
          textToApply = await postprocessDictation(transcript, userId, token);
        } catch (err) {
          logger.warn("dictation_postprocess_failed err=%o", err);
          showPostprocessWarning(
            "Post-process unavailable — capitalization / abbreviations not applied.",
          );
        }
      }
      const next =
        mode === "literal"
          ? spliceAtCaret(stripTarget.originalText, textToApply, stripTarget.caretOffset)
          : textToApply;
      stripTarget.apply(next);
      appendDictationHistory(store, {
        ts: Date.now(),
        text: transcript,
        postprocessed: textToApply,
        mode: store.get(dictationSessionAtom).mode,
        dispatch: {
          kind: "insert",
          targetChipId: stripTarget.rowId,
          pos: null,
          sectionKind: null,
        },
      });
    },
    [store, token, userId, showPostprocessWarning],
  );

  // Literal mode (Shift+`): insert at caret without using the chip as a
  // rewrite target. If the snapshot was in a chip, splice the dictation
  // into the chip's text at the caret offset captured at backtick-down
  // (read from the browser Selection API -
  // same offset the ghost rendered at). Otherwise insert plain text at the
  // PM caret pos.
  const applyLiteralDictation = useCallback(
    (ed: Editor, transcript: string, textToInsert: string, target: EditorDictationTarget) => {
      const { focus: focusSnapshot, pos } = target;
      const literalChipId = getFocusedChipId(focusSnapshot);
      const literalChip = literalChipId ? findFieldNodeById(ed.state.doc, literalChipId) : null;
      if (literalChipId && literalChip) {
        const existing = (literalChip.node.attrs.text as string) ?? "";
        const merged = spliceAtCaret(existing, textToInsert, target.caretOffset);
        const applied = ed
          .chain()
          .focus()
          .command(({ tr }) => {
            tr.setMeta(dictationGhostPluginKey, null);
            return true;
          })
          .replaceChipText(literalChipId, merged)
          .run();
        if (!applied) throw new Error("Report editor rejected dictation.");
        // Chain cleared the plugin ghost atomically; clear the chip
        // ghost atom too so the inline NodeView ghost goes away.
        jotaiStore.set(dictationGhostChipAtom, null);
        appendDictationHistory(store, {
          ts: Date.now(),
          text: transcript,
          postprocessed: textToInsert,
          mode: store.get(dictationSessionAtom).mode,
          dispatch: {
            kind: "insert",
            targetChipId: literalChipId,
            pos: null,
            sectionKind:
              focusSnapshot.mode === "chip-focused" || focusSnapshot.mode === "caret-in-chip"
                ? focusSnapshot.sectionKind
                : null,
          },
        });
        return;
      }
      if (literalChipId) throw new Error("Dictation target no longer exists.");
      if (pos === null) throw new Error("Dictation destination was removed.");
      const applied = ed
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.setMeta(dictationGhostPluginKey, null);
          return true;
        })
        .setTextSelection(pos)
        .insertContent(textToInsert)
        .run();
      if (!applied) throw new Error("Report editor rejected dictation.");
      appendDictationHistory(store, {
        ts: Date.now(),
        text: transcript,
        postprocessed: textToInsert,
        mode: store.get(dictationSessionAtom).mode,
        dispatch: {
          kind: "insert",
          targetChipId: null,
          pos,
          sectionKind: focusSnapshot.mode === "caret-in-prose" ? focusSnapshot.sectionKind : null,
        },
      });
    },
    [store],
  );

  // A removed chip invalidates the destination. Never redirect spoken text
  // into another section; the delivery handler retains it for recovery.
  const applySmartDictation = useCallback(
    (ed: Editor, transcript: string, textToInsert: string, target: EditorDictationTarget) => {
      const { focus: focusSnapshot, pos } = target;
      const snapshotChipId = getFocusedChipId(focusSnapshot);
      if (snapshotChipId) {
        const stillExists = findFieldNodeById(ed.state.doc, snapshotChipId);
        if (stillExists) {
          // Combine ghost clear + chip text update in one chain so there's
          // no intermediate paint where the ghost is gone but the text
          // hasn't landed yet.
          const applied = ed
            .chain()
            .focus()
            .command(({ tr }) => {
              tr.setMeta(dictationGhostPluginKey, null);
              return true;
            })
            .replaceChipText(snapshotChipId, textToInsert)
            .run();
          if (!applied) throw new Error("Report editor rejected dictation.");
          jotaiStore.set(dictationGhostChipAtom, null);
          appendDictationHistory(store, {
            ts: Date.now(),
            text: transcript,
            postprocessed: textToInsert,
            mode: store.get(dictationSessionAtom).mode,
            dispatch: {
              kind: "insert",
              targetChipId: snapshotChipId,
              pos: null,
              sectionKind:
                focusSnapshot.mode === "chip-focused" || focusSnapshot.mode === "caret-in-chip"
                  ? focusSnapshot.sectionKind
                  : null,
            },
          });
          return;
        }
        throw new Error("Dictation target no longer exists.");
      }

      if (pos === null) throw new Error("Dictation destination was removed.");
      // caret-in-prose: insert a new field at the captured caret.
      // Cursor inside an existing field → replace its content with the
      // dictation. Cursor outside any field → wrap the dictation in a new
      // field mark, which renders as a Tab-stop with dimmed brackets via
      // CSS pseudo-elements (no `[` `]` characters in the document).
      const dictation = buildDictationContent(textToInsert);
      const inField = findFieldAtCursor(ed, pos);
      const chain = ed
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.setMeta(dictationGhostPluginKey, null);
          return true;
        });
      if (inField) {
        chain.setTextSelection({ from: inField.start, to: inField.end }).insertContent(dictation);
      } else {
        chain.setTextSelection(pos).insertContent([...dictation, { type: "text", text: " " }]);
      }
      if (!chain.run()) throw new Error("Report editor rejected dictation.");
      appendDictationHistory(store, {
        ts: Date.now(),
        text: transcript,
        postprocessed: textToInsert,
        mode: store.get(dictationSessionAtom).mode,
        dispatch: {
          kind: "insert",
          targetChipId: null,
          pos: pos,
          sectionKind: focusSnapshot.mode === "caret-in-prose" ? focusSnapshot.sectionKind : null,
        },
      });
    },
    [findFieldAtCursor, store],
  );

  const handleDictationResult = useCallback(
    async (transcript: string) => {
      const ed = editorRef.current;
      // Keep the captured target alive through cleanup so the preview stays
      // at that destination. dispatchTranscript clears it after application.
      const stripTarget = stripDictationTargetRef.current;
      if (stripTarget) {
        await applyStripDictation(stripTarget, transcript);
        return;
      }

      if (!ed || ed.isDestroyed) throw new Error("Report editor is unavailable.");
      if (!transcript.trim()) {
        appendDictationHistory(store, {
          ts: Date.now(),
          text: transcript,
          postprocessed: "",
          mode: store.get(dictationSessionAtom).mode,
          dispatch: { kind: "dropped", reason: "empty" },
        });
        emit("toast", { message: "No speech detected - try dictating again." });
        return;
      }

      // Capture chip identity at gesture start; map prose positions through
      // editor transactions until insertion. Failed destinations are recoverable.
      const target = editorDictationTargetRef.current;
      if (!target || target.focus.mode === "none") {
        throw new Error("Dictation has no report destination.");
      }

      const mode = dictationModeRef.current;

      // Dictation post-processing — both smart and literal: capitalize
      // first letter, abbreviate "cm" / "mm". On failure, insert the raw
      // transcript so the rad's words aren't lost — a deliberate
      // fallback because losing spoken content is worse than skipping
      // capitalization. The smart/literal distinction is downstream of
      // this: smart replaces the focused chip's content, literal inserts
      // text at the caret without touching chip identity.
      let textToInsert = transcript;
      if (userId && token) {
        try {
          textToInsert = await postprocessDictation(transcript, userId, token);
        } catch (err) {
          logger.warn("dictation_postprocess_failed err=%o", err);
          showPostprocessWarning(
            "Post-process unavailable — capitalization / abbreviations not applied.",
          );
        }
      }

      if (ed.isDestroyed) throw new Error("Report editor is unavailable.");
      if (mode === "literal") {
        applyLiteralDictation(ed, transcript, textToInsert, target);
        return;
      }
      applySmartDictation(ed, transcript, textToInsert, target);
    },
    [
      applyStripDictation,
      applyLiteralDictation,
      applySmartDictation,
      setGhostText,
      showPostprocessWarning,
      store,
      token,
      userId,
    ],
  );

  // Forward refs so the router below stays stable while the underlying
  // handlers re-memoize. The same speech pipeline serves all three chords
  // (` smart, Shift+` literal, Ctrl+` voice_adjust); the mode flipped at
  // keydown time decides which handler runs at finalize.
  const handleDictationResultRef = useRef<((t: string) => Promise<void>) | null>(null);
  const handleVoiceAdjustTranscriptRef = useRef<((t: string) => Promise<void>) | null>(null);
  const dispatchTranscript = useCallback(
    async (transcript: string) => {
      const handler =
        dictationModeRef.current === "voice_adjust"
          ? handleVoiceAdjustTranscriptRef.current
          : handleDictationResultRef.current;
      if (!handler) throw new Error("Report editor is unavailable.");
      try {
        await handler(transcript);
      } finally {
        resetDictationGesture();
      }
    },
    [resetDictationGesture],
  );
  handleDictationResultRef.current = handleDictationResult;
  // Bridge: keep the outer provider's stable onFinalTranscript callback pointing
  // at the current dispatch function so the WS can be mounted above this component.
  dispatchTranscriptRef.current = dispatchTranscript;

  // reporter-jobs: auto-connect dictation on mount so the backtick listener
  // (gated on isDictationReady) actually registers. Without this the rad has
  // to click "Dictate" before ` and ⌃` are intercepted — until they do,
  // pressing ` types a literal backtick into the editor.
  const rjAutoConnectedRef = useRef(false);
  useEffect(() => {
    if (!userId || !token) return;
    if (rjAutoConnectedRef.current) return;
    if (isDictationReady || isConnecting) return;
    rjAutoConnectedRef.current = true;
    connect();
  }, [userId, token, isDictationReady, isConnecting, connect]);

  const showImpressionWarning = useCallback((message: string) => emit("toast", { message }), []);

  const showDictationWarning = useCallback((message: string) => emit("toast", { message }), []);

  const showVoiceAdjustWarning = useCallback((message: string) => emit("toast", { message }), []);

  // Voice-adjust transcript handler. Routed via dispatchTranscript when
  // dictationModeRef is "voice_adjust" — same dictation WS as raw dictation,
  // mode flag is the only gate.
  const handleVoiceAdjustTranscript = useCallback(
    async (instruction: string) => {
      // Strip-row target takes precedence — strip-mode tune routes through
      // the card's local override, not the editor.
      const stripTarget = stripTuneTargetRef.current;
      stripTuneTargetRef.current = null;
      if (stripTarget) {
        if (!instruction.trim()) {
          appendDictationHistory(store, {
            ts: Date.now(),
            text: instruction,
            postprocessed: "",
            mode: store.get(dictationSessionAtom).mode,
            dispatch: { kind: "dropped", reason: "empty" },
          });
          setVoiceAdjustState("idle");
          return;
        }
        try {
          setVoiceAdjustState("adjusting");
          if (!userId || !token) throw new Error("Auth not ready");
          const modified = await voiceAdjust(stripTarget.originalText, instruction, userId, token);
          if (modified.trim()) {
            stripTarget.apply(modified.trim());
            voiceTuneCountRef.current += 1;
          }
          appendDictationHistory(store, {
            ts: Date.now(),
            text: instruction,
            postprocessed: modified.trim(),
            mode: store.get(dictationSessionAtom).mode,
            dispatch: { kind: "insert", targetChipId: null, pos: null, sectionKind: null },
          });
        } catch (err) {
          logger.warn("dictation_strip_voice_adjust_failed err=%o", err);
          showVoiceAdjustWarning(
            err instanceof Error ? `Refine failed: ${err.message}` : "Refine failed.",
          );
          appendDictationHistory(store, {
            ts: Date.now(),
            text: instruction,
            postprocessed: "",
            mode: store.get(dictationSessionAtom).mode,
            dispatch: { kind: "dropped", reason: "postprocess" },
          });
        } finally {
          setVoiceAdjustState("idle");
        }
        return;
      }

      const target = voiceAdjustTargetRef.current;
      voiceAdjustTargetRef.current = null;
      if (!target || !instruction.trim()) {
        if (target) {
          // Distinguish a tap (no audio captured) from a normal hold that
          // produced no transcript. Audio chunks fire every 256ms; under
          // 350ms means almost certainly nothing reached the transcriber.
          const heldMs = Date.now() - voiceAdjustHoldStartRef.current;
          showVoiceAdjustWarning(
            heldMs < 350
              ? "Hold ctrl+` while speaking — release was too quick."
              : "No instruction heard. Try speaking again, closer to the mic.",
          );
          appendDictationHistory(store, {
            ts: Date.now(),
            text: instruction,
            postprocessed: "",
            mode: store.get(dictationSessionAtom).mode,
            dispatch: { kind: "dropped", reason: "empty" },
          });
        }
        setVoiceAdjustState("idle");
        return;
      }
      try {
        setVoiceAdjustState("adjusting");
        if (!userId || !token) throw new Error("Auth not ready");
        const modified = await voiceAdjust(target.originalText, instruction, userId, token);
        const ed = editorRef.current;
        if (!ed || ed.isDestroyed || !modified.trim()) return;
        const fieldType = ed.schema.nodes.field;
        const replacementText = modified.trim();
        const tr = ed.state.tr;
        if (target.markAttrs) {
          // Selection was inside a single field. Re-locate the field by its
          // stable attrs.id rather than trusting fieldStart/fieldEnd captured
          // at keydown — the ~11s async window (transcription finalize + voiceAdjust
          // LLM call) is long enough for EmptyFieldGC, EmptyImpressionItemGC,
          // the impression-stream renderer, and other appendTransactions to
          // shift positions. Replacing at the captured range can rewrite the
          // wrong chip or throw RangeError.
          const targetId = (target.markAttrs.id as string | null) ?? null;
          let liveStart: number | null = null;
          let liveEnd: number | null = null;
          if (targetId) {
            tr.doc.descendants((node, pos) => {
              if (liveStart !== null) return false;
              if (node.type.name !== "field") return true;
              if (node.attrs.id === targetId) {
                liveStart = pos;
                liveEnd = pos + node.nodeSize;
                return false;
              }
              return false;
            });
          }
          if (liveStart === null || liveEnd === null) {
            showVoiceAdjustWarning("Target chip no longer exists — refine skipped.");
            return;
          }
          const newField = fieldType.create({
            ...(target.markAttrs as Record<string, unknown>),
            text: replacementText ?? "",
          });
          tr.replaceWith(liveStart, liveEnd, newField);
        } else {
          // Plain prose selection. The captured range can't be re-located by
          // id (no field involved). Verify the range is still in-bounds and
          // its text matches the snapshot taken at keydown; if either has
          // drifted during the await, abort — guessing at the rad's intent
          // is worse than asking them to redo the selection.
          const docSize = tr.doc.content.size;
          if (
            target.fieldStart < 0 ||
            target.fieldEnd > docSize ||
            target.fieldStart >= target.fieldEnd
          ) {
            showVoiceAdjustWarning("Selection no longer valid — refine skipped.");
            return;
          }
          const liveText = tr.doc.textBetween(target.fieldStart, target.fieldEnd);
          if (liveText !== target.originalText) {
            showVoiceAdjustWarning("Selection text changed — refine skipped.");
            return;
          }
          tr.replaceWith(target.fieldStart, target.fieldEnd, ed.schema.text(replacementText));
        }
        ed.view.dispatch(tr);
        voiceTuneCountRef.current += 1;
        appendDictationHistory(store, {
          ts: Date.now(),
          text: instruction,
          postprocessed: modified.trim(),
          mode: store.get(dictationSessionAtom).mode,
          dispatch: {
            kind: "insert",
            targetChipId: null,
            pos: target.fieldStart,
            sectionKind: null,
          },
        });
      } catch (err) {
        logger.warn("dictation_voice_adjust_failed err=%o", err);
        showVoiceAdjustWarning(
          err instanceof Error ? `Refine failed: ${err.message}` : "Refine failed.",
        );
        appendDictationHistory(store, {
          ts: Date.now(),
          text: instruction,
          postprocessed: "",
          mode: store.get(dictationSessionAtom).mode,
          dispatch: { kind: "dropped", reason: "postprocess" },
        });
      } finally {
        setVoiceAdjustState("idle");
      }
    },
    [showVoiceAdjustWarning, store, token, userId],
  );

  handleVoiceAdjustTranscriptRef.current = handleVoiceAdjustTranscript;

  // Surface dictation/voice-adjust hook errors as a toast. Hook silently
  // retries up to 3 times then sets `error` — without a toast the rad
  // sees no feedback and presses Dictate again into a dead WS.
  useEffect(() => {
    if (!dictationHookError) return;
    showDictationWarning(dictationHookError);
  }, [dictationHookError, showDictationWarning]);

  useEffect(() => {
    if (!dictationHookError) return;
    if (stage !== "listening" && stage !== "finalizing" && stage !== "delivering") {
      resetDictationGesture();
    }
  }, [dictationHookError, stage, resetDictationGesture]);

  // HFP narrowband warning — what production dictation apps (e.g. Wispr
  // Flow) do when the picked mic is in bluetooth phone-call mode. Tell
  // the rad upfront that quality will be limited and point them at the
  // built-in mic in the picker instead of letting them silently get
  // worse transcription. Fires once per mic-acquisition (audioQuality is
  // null until getUserMedia resolves; flips to "narrowband" or "wideband"
  // and stays until the next mic switch resets state).
  useEffect(() => {
    if (audioQuality !== "narrowband") return;
    showDictationWarning(
      "Bluetooth mic is in narrowband mode — switch to a built-in or wired mic for better dictation accuracy.",
    );
  }, [audioQuality, showDictationWarning]);

  // Clear timers + media tracks on unmount; clear hovered-surface so a
  // stale 'editor' value doesn't gate viewer hotkeys when this report is
  // unmounted (e.g. nav to next case).
  //
  // This is unmount-only logic: deps must be empty. If we listed
  // `cancelDictation` it would re-run every time the hook's
  // `cancel` callback re-memoized (which happens whenever
  // `state.isRecording` flips), and the cleanup would reset
  // `voiceAdjustHeldRef` to false during a hold — leaving keyup with
  // nothing to do and the recording stuck.
  const cancelDictationRef = useRef(cancelDictation);
  cancelDictationRef.current = cancelDictation;
  useEffect(() => {
    return () => {
      resetDictationGesture();
      dispatchTranscriptRef.current = null;
      cancelDictationRef.current();
      setHoveredSurface((prev) => (prev === "editor" ? null : prev));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update ghost decoration with the live transcript so the user sees
  // continuous feedback while dictating. Renders the canonical two-zone
  // shape (finalized + interim) as one string — finalized is the
  // completed transcript of this hold, interim is the
  // volatile trailing transcript. Mic test panel reads the same atom so
  // both surfaces show identical text.
  useEffect(() => {
    if ((isDictating || isProcessing) && dictationModeRef.current !== "voice_adjust") {
      const ghost = [liveTranscript.finalized, liveTranscript.interim].filter(Boolean).join(" ");
      setGhostText(ghost || null);
    } else {
      setGhostText(null);
    }
  }, [liveTranscript.finalized, liveTranscript.interim, isDictating, isProcessing, setGhostText]);

  // Begin smart/literal dictation targeting the editor caret (or focused
  // chip). Shared by the hold-backtick keydown handler AND the visible mic
  // button so both snapshot the SSoT + caret offset identically and route
  // through the same finalize path (handleDictationResult). Returns whether
  // the dictation stream actually started.
  const beginEditorDictation = useCallback(
    (mode: DictationMode): boolean => {
      const ed = editorRef.current;
      if (!ed || ed.isDestroyed) return false;
      if (isBacktickHeldRef.current || voiceAdjustHeldRef.current) return false;
      if (jotaiStore.get(dictationStageAtom) !== "ready") return false;
      const focus = jotaiStore.get(reportFocusAtom);
      if (focus.mode === "none") {
        showDictationWarning("Click into the report or focus a chip before dictating.");
        return false;
      }
      // Caret offset within the focused chip's inner contenteditable — null
      // when the caret is in prose or the chip is Tab-focused as a whole.
      let caretOffset: number | null = null;
      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl?.classList.contains(CHIP_INNER_CLASS)) {
        const domSel = window.getSelection();
        if (domSel && domSel.rangeCount > 0) {
          const range = domSel.getRangeAt(0);
          if (range.collapsed && activeEl.contains(range.startContainer)) {
            caretOffset = range.startOffset;
          }
        }
      }
      isBacktickHeldRef.current = true;
      editorDictationTargetRef.current = { focus, pos: ed.state.selection.from, caretOffset };
      dictationModeRef.current = mode;
      jotaiStore.set(dictationSessionAtom, (s) => ({ ...s, mode }));
      const started = startDictation();
      if (!started) resetDictationGesture();
      return started;
    },
    [startDictation, showDictationWarning, resetDictationGesture],
  );

  // A click toggles smart dictation from the visible mic button: start if
  // idle, stop if this same path is recording. Voice-adjust holds (ctrl+`)
  // use voiceAdjustHeldRef, which beginEditorDictation guards against.
  const toggleEditorDictation = useCallback(() => {
    if (isBacktickHeldRef.current) {
      isBacktickHeldRef.current = false;
      stopDictation();
      return;
    }
    beginEditorDictation("smart");
  }, [beginEditorDictation, stopDictation]);

  // Push-to-talk dictation. Three chords, each maps to a DictationMode:
  //   `        — smart    (dictate + post-process: capitalization, cm/mm abbr)
  //   Shift+`  — literal  (dictate, raw transcript, no post-process)
  //   Ctrl+`   — voice_adjust (refine the focused chip / strip card via LLM)
  // Smart and literal insert at cursor (chip or prose). Voice_adjust
  // requires a refine target — focused strip card OR a chip containing
  // real text at the cursor — and aborts with a toast otherwise.
  //
  // Native listener instead of useHotkeys — react-hotkeys-hook (hotkeys-js
  // under the hood) silently fails to fire on "`". Capture phase +
  // stopPropagation so the event never reaches TipTap's ProseMirror
  // keydown handler. ProseMirror inserts characters via direct DOM
  // mutation, not the browser default, so a bubble-phase preventDefault
  // alone isn't enough.
  //
  // Listeners install once per PrototypeReport mount — backtick is
  // reserved for dictation for the editor's entire lifetime on this
  // page. start() self-gates via its boolean return, so no stage check
  // needed here.
  useEffect(() => {
    // Match the physical key (e.code === "Backquote") in addition to the
    // logical key (e.key === "`"). Firefox emits e.key === "Dead" when
    // backtick acts as a combining accent on US-International / Mac UK
    // layouts, which broke dictation in Firefox while Chrome was fine.
    const isBacktick = (e: KeyboardEvent) => e.key === "`" || e.code === "Backquote";

    // Resolve the chord → mode. Returns null for unknown modifier combos
    // (Alt, Meta, or two-modifier chords) so we don't claim those keys.
    const modeForChord = (e: KeyboardEvent): DictationMode | null => {
      if (e.altKey || e.metaKey) return null;
      if (e.ctrlKey && e.shiftKey) return null;
      if (e.ctrlKey) return "voice_adjust";
      if (e.shiftKey) return "literal";
      return "smart";
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isBacktick(e)) return;
      const mode = modeForChord(e);
      if (!mode) return;
      const target = e.target as HTMLElement | null;
      // Don't fight with inputs/textareas elsewhere — but the InlineChipCard
      // textarea AND the strip card input are valid backtick targets, so
      // backtick there should route through refine on that surface (not
      // type a literal `).
      const inChipCard =
        !!target?.closest(".rj-inline-card") || !!target?.closest(".suggestion-strip__card");
      if (!inChipCard && (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA")) return;
      // Always claim the key — even on repeat events while held — so neither
      // the browser nor ProseMirror inserts "`" into a contenteditable.
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat || isBacktickHeldRef.current || voiceAdjustHeldRef.current) return;
      if (jotaiStore.get(dictationStageAtom) !== "ready") return;

      // activeElement decides which surface the rad expects dictation on
      // (strip card vs editor). The editor caret/chip-offset snapshot itself
      // is taken inside beginEditorDictation, on the editor path below.
      const activeEl = document.activeElement as HTMLElement | null;

      // DOM focus is ground truth for "which surface does the rad expect
      // dictation on?" — focusedStripRowAtom isn't cleared when the rad
      // moves caret back to the editor, so reading the atom alone routes
      // to a stale strip card. Only treat a strip card as the target when
      // activeElement is actually inside it.
      const inStripCard = !!activeEl?.closest(".suggestion-strip__card");

      if (mode === "voice_adjust") {
        // Resolve refine target — strip card takes precedence over chip.
        const stripFocused = inStripCard ? focusedStripRowRef.current : null;
        if (stripFocused) {
          stripTuneTargetRef.current = {
            rowId: stripFocused.id,
            originalText: stripFocused.originalText,
            apply: stripFocused.apply,
          };
        } else {
          const ed = editorRef.current;
          if (!ed || ed.isDestroyed) {
            showVoiceAdjustWarning("Focus a chip or strip card to refine.");
            return;
          }
          // Chip target: trust reportFocusAtom (set by ChipView's
          // handleInnerFocus when the rad clicks/Tabs into the chip's
          // inner contenteditable) over PM selection.from. The inner
          // contenteditable lives inside a NodeView outside PM's caret
          // tracking, so sel.from can stay stale on a previously-touched
          // field — that's how `ctrl+` in pancreas` was landing on the
          // bones chip.
          let refineTarget: typeof voiceAdjustTargetRef.current = null;
          const focusedChipId = getFocusedChipId(jotaiStore.get(reportFocusAtom));
          if (focusedChipId) {
            const hit = findFieldNodeById(ed.state.doc, focusedChipId);
            if (hit) {
              const text = (hit.node.attrs.text as string | null) ?? "";
              if (text.trim() && text !== "​") {
                refineTarget = {
                  fieldStart: hit.pos,
                  fieldEnd: hit.pos + hit.node.nodeSize,
                  originalText: text,
                  markAttrs: { ...hit.node.attrs },
                };
              }
            }
          }
          // Fallback: highlighted text selection (prose or chip range).
          // Only when no focused chip — selection-based refine is for the
          // rad explicitly marking a span to rewrite.
          if (!refineTarget) {
            const sel = ed.state.selection;
            if (!sel.empty) {
              const text = ed.state.doc.textBetween(sel.from, sel.to);
              if (text.trim()) {
                const enclosing = findFieldAtCursor(ed, sel.from);
                const insideField = !!enclosing && sel.to <= enclosing.end;
                const fieldNode =
                  insideField && enclosing ? ed.state.doc.nodeAt(enclosing.start) : null;
                const isWholeField = !!enclosing && !!fieldNode && fieldNode.type.name === "field";
                refineTarget = {
                  fieldStart: isWholeField && enclosing ? enclosing.start : sel.from,
                  fieldEnd: isWholeField && enclosing ? enclosing.end : sel.to,
                  originalText: text,
                  markAttrs: isWholeField && fieldNode ? { ...fieldNode.attrs } : null,
                };
              }
            }
          }
          if (!refineTarget) {
            showVoiceAdjustWarning("Focus a chip or select text to refine.");
            return;
          }
          voiceAdjustTargetRef.current = refineTarget;
        }
        voiceAdjustHeldRef.current = true;
        voiceAdjustHoldStartRef.current = Date.now();
        dictationModeRef.current = "voice_adjust";
        jotaiStore.set(dictationSessionAtom, (s) => ({ ...s, mode: "voice_adjust" }));
        setVoiceAdjustState("recording");
        const started = startDictation();
        if (!started) {
          resetDictationGesture();
          showVoiceAdjustWarning("Refine failed to start — try again.");
        }
        return;
      }

      // smart | literal — dictate at cursor. Strip card wins only when
      // the rad's DOM focus is actually inside one; the atom alone is
      // stale after the rad moves back to the editor (no blur-cleanup
      // on the strip side), so reading it without the inStripCard gate
      // would route editor dictation to a stale strip card.
      const stripFocused = inStripCard ? focusedStripRowRef.current : null;
      if (stripFocused) {
        const inputEl = activeEl?.tagName === "INPUT" ? (activeEl as HTMLInputElement) : null;
        const caretOffset = inputEl?.selectionStart ?? null;
        stripDictationTargetRef.current = {
          rowId: stripFocused.id,
          originalText: stripFocused.originalText,
          apply: stripFocused.apply,
          caretOffset,
        };
        isBacktickHeldRef.current = true;
        dictationModeRef.current = mode;
        jotaiStore.set(dictationSessionAtom, (s) => ({ ...s, mode }));
        if (!startDictation()) resetDictationGesture();
        return;
      }

      // Editor caret (or focused chip): snapshot the SSoT + pos and start via
      // the shared helper so hold-backtick and the mic button route through
      // the same finalize path (handleDictationResult routes by chipId when
      // available, else pos).
      beginEditorDictation(mode);
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (!isBacktick(e)) return;
      if (!isBacktickHeldRef.current && !voiceAdjustHeldRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      if (isBacktickHeldRef.current) {
        isBacktickHeldRef.current = false;
        stopDictation();
      }
      if (voiceAdjustHeldRef.current) {
        voiceAdjustHeldRef.current = false;
        // Only claim "transcribing" if capture actually went live. Since the
        // mic now opens lazily on the first press (warm-up no longer acquires
        // it), a denied/failed first-press acquire leaves the hook at "ready"
        // and start() returns true before capture begins — so the keydown's
        // `if (!started)` guard never reset us. Reading the live stage here is
        // the honest signal: "listening" (or a processing stage if the
        // transcriber already started finalizing) means audio flowed; anything else means
        // the mic never opened, so drop back to idle instead of stranding the
        // "Transcribing…" overlay forever.
        const liveStage = jotaiStore.get(dictationStageAtom);
        const captureWentLive =
          liveStage === "listening" || liveStage === "finalizing" || liveStage === "delivering";
        if (captureWentLive) {
          setVoiceAdjustState("transcribing");
          stopDictation();
        } else {
          // Capture never went live. Cancel any in-flight start so the hook's
          // post-acquire intent re-check stops a stream that resolves AFTER
          // this keyup (fast press+release before the first-press mic acquire
          // settles) — otherwise the mic would go live with no key held.
          // cancel() no-ops harmlessly when the acquire already failed.
          cancelDictationRef.current();
          resetDictationGesture();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
    };
  }, [
    findFieldAtCursor,
    showVoiceAdjustWarning,
    startDictation,
    stopDictation,
    beginEditorDictation,
    resetDictationGesture,
  ]);

  // Escape cancels an in-flight dictation without committing the transcript.
  useHotkeys(
    "escape",
    () => {
      resetDictationGesture();
      cancelDictation();
    },
    {
      enabled: isDictating,
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [isDictating, cancelDictation, resetDictationGesture],
  );

  // Resolve initial editor content at mount — saved draft/signed report takes
  // precedence over the fresh LLM template. Derived once; subsequent changes
  // are surgical (see streamImpression, handleToConsiderAccept, etc.).
  // Drafts from older schemas (pre-FieldMark-redesign or pre-cutover-to-
  // typed-sections) don't load cleanly in the current editor. Detect at
  // mount and surface the Reset Draft banner so the rad sees the LLM
  // template instead of an empty editor.
  const schemaStaleDraftDetected = useMemo(
    () => isStaleDraft(reportState?.structured_editor_state),
    [reportState?.structured_editor_state],
  );

  // The backend selects a base, composed, or safe generic layout from canonical
  // study coverage and model compartments. There is no frontend exam default.
  const EXAM_TYPE = reportExamType;
  const globalTemplate = effectiveTemplate;
  const staleDraftDetected = schemaStaleDraftDetected || templateMismatchDraftDetected;

  // Out-of-region guard (web-demo-backend#598), for a region we detect but have
  // no template for yet. `buildReportDoc` iterates `template.findings`, not the
  // API's `sections` keys, and emits `sub.default` for every kind with no
  // findings — so the backend dropping those sections is not enough on its own:
  // the editor would re-emit all 15 abdominopelvic normal statements ("Normal
  // liver.", "No hernia.") about anatomy that was never imaged. Restrict the
  // template to the kinds the backend actually returned. Only this reporter path
  // filters — template editing (MyTemplates / TemplateEditor) still shows the
  // full section set.
  const template = useMemo(() => {
    if (!globalTemplate || !reportData) return globalTemplate;
    return templateForReportSections(
      globalTemplate,
      reportData.sections,
      reportData.meta.templateMismatch,
    );
  }, [globalTemplate, reportData]);

  // Blank-template projects (`templateMode === "blank"`) skip the AI
  // /api/report fetch — reportData is undefined. We still want the editor
  // to render the negative template (Lower chest is clear, Normal liver,
  // etc.) instead of opening empty, so fall back to an empty sections map
  // and let buildReportDoc emit each subsection's `sub.default` text.
  //
  // A/B mode gate: only the `inline` arm folds AI findings into the editor.
  // `strip` keeps the editor at template defaults — the strip column above
  // the editor shows confident + maybe as cards; accept promotes a card
  // into the doc. `off` is the baseline arm with no AI anywhere. Both
  // strip and off start from the same empty-AI editor doc.
  //
  // Review-tier ("maybe") findings are additionally gated by the gear
  // toggle (default off), so a fresh inline draft omits them unless opted in.
  const { sectionsForDoc, toConsiderForDoc } = useMemo(
    () => ({
      sectionsForDoc: reporterAiMode === "inline" ? (reportData?.sections ?? {}) : {},
      toConsiderForDoc:
        reporterAiMode === "inline" && showLowConfidenceFindings
          ? (reportData?.toConsider ?? [])
          : [],
    }),
    [reporterAiMode, reportData, showLowConfidenceFindings],
  );
  const canRebuildDraft = !aiTemplateEnabled || isWordingReady(reportData?.meta.wording);
  // All case-document construction (initial, recovery and reset) crosses the
  // same readiness boundary. Reading a saved document never requires rebuilding it.
  const buildFreshDraft = useCallback(() => {
    if (aiTemplateEnabled) requireReadyWording(reportData?.meta.wording);
    return template ? buildReportDoc(template, sectionsForDoc, {}, toConsiderForDoc) : "";
  }, [aiTemplateEnabled, reportData, template, sectionsForDoc, toConsiderForDoc]);
  const initialContent =
    initialSigned && reportState?.structured_editor_state
      ? reportState.structured_editor_state
      : staleDraftDetected && canRebuildDraft
        ? buildFreshDraft()
        : (reportState?.structured_editor_state ?? buildFreshDraft());

  const editor = useEditor({
    extensions: [
      StarterKit,
      FieldNode,
      FindingsSectionNode,
      ImpressionSectionNode,
      OrganSubsectionNode,
      ImpressionItemNode,
      FieldNavigation,
      VerticalArrowNav,
      ImpressionParagraphAttrs,
      AddOrganFindingExtension,
      AddImpressionItemExtension,
      FindingToImpressionDropExtension,
      RegenerateImpressionExtension,
      DragHandle.configure({
        render: () => {
          const el = document.createElement("button");
          el.type = "button";
          el.className = "impression-drag-handle";
          el.tabIndex = -1;
          el.setAttribute("aria-label", "Drag to reorder");
          el.textContent = "⠿";
          return el;
        },
        // Enable nested mode so the handle targets impressionItem nodes
        // inside impressionSection (not the section itself). Without this,
        // the default targets top-level blocks and we'd be dragging the
        // whole impression section instead of individual items.
        nested: {
          allowedContainers: ["impressionSection"],
          rules: [
            {
              id: "impressionItemOnly",
              evaluate: ({ node }) => (node.type.name === "impressionItem" ? -1000 : 1000),
            },
          ],
        },
        // CSS fades opacity to 0 when data-target-node isn't impressionItem,
        // so top-level blocks (organSubsection, findingsSection, the
        // impressionSection itself) get no visible handle. We don't clear
        // on null — keeping the last positive value avoids flicker between
        // blocks and matches the extension's own visibility behavior.
        onNodeChange: ({ node }) => {
          if (!node) return;
          const dom = document.querySelector<HTMLElement>(".impression-drag-handle");
          if (!dom) return;
          dom.dataset.targetNode = node.type.name;
        },
      }),
      DictationGhostExtension,
      ReportStructureGuard.configure({
        isLocked: () => !!readerContext && (initialSigned || pendingReaderSign.current !== null),
      }),
      EmptyImpressionItemGC,
    ],
    content: initialContent,
    editable: !initialSigned,
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none focus:outline-none min-h-[200px] p-4",
        // On the ProseMirror root itself, so E2E gates the contenteditable rather
        // than the `.report-editor .tiptap` CSS pair (class locators are banned by
        // CLAUDE.md and break when styling changes). Same element the pair
        // resolved to, so locator semantics are unchanged.
        "data-testid": "report-editor",
      },
      // Multi-line pastes would overflow a single-paragraph organSubsection
      // and cause PM to spawn rogue subsections to make room. Collapse all
      // newlines so the paste fits the cursor's chip / paragraph cleanly.
      transformPastedText: (text) => text.replace(/\s*\n+\s*/g, " ").trim(),
      transformPastedHTML: (html) =>
        html
          .replace(/<\/(p|div|h[1-6]|br)>/gi, " ")
          .replace(/<br\s*\/?>/gi, " ")
          .replace(/<[^>]+>/g, "") // strip remaining tags
          .replace(/\s+/g, " ")
          .trim(),
      // Atomic chips: typed text between chips lives as ordinary prose in
      // the paragraph's inline content. The previous "wrap any typed char
      // into a field" interceptor doesn't fit — chips are created via the
      // "+ Add finding" button, dictation, or AI suggestion accept.
    },
  });

  // Right-click "Rename / Remove section" on an organ-subsection label —
  // shared with the template editor via this hook (label inert on left-click).
  const sectionContextMenu = useSectionContextMenu(editor);

  // Editor population + impression recovery. Two concerns merged:
  //
  // (a) `useEditor` runs once at mount with whatever `initialContent` was at
  //     first render. If `template` (or `reportData` on AI-template projects)
  //     hadn't resolved yet, initialContent was "" and the editor mounted
  //     empty. Repopulate once the deps arrive.
  //
  // (b) The mount-only impression-stream effect at :1514 only depends on
  //     `[editor]`, so it captures the closure of reportData at editor-
  //     identity-stable time. Two paths it can't reach: cold-cache mounts
  //     where reportData was undefined when the editor was created, and
  //     stale-draft mounts where it bails on `reportState.structured_editor_state`
  //     being present. Fire the impression here for those paths, gated by a
  //     ref so we don't double-fire when the mount-only effect already ran.
  const recoveryImpressionFiredRef = useRef(false);
  useEffect(() => {
    if (!editor || initialSigned) return;
    if (!template) return;
    if (!canRebuildDraft) return;
    const wasEmpty = editor.state.doc.content.size <= 2;
    if (wasEmpty) {
      const fresh = buildFreshDraft();
      editor.commands.setContent(fresh, { emitUpdate: false });
      onReportContentApplied?.();
    }
    // Fire impression only when the mount-only effect couldn't (cold-cache
    // empty mount, or stale-draft path that bails on saved state). Skip on
    // blank-template projects — no AI findings to summarize.
    if (
      aiTemplateEnabled &&
      !recoveryImpressionFiredRef.current &&
      (staleDraftDetected || wasEmpty)
    ) {
      recoveryImpressionFiredRef.current = true;
      streamImpressionRef.current?.();
    }
  }, [
    editor,
    initialSigned,
    staleDraftDetected,
    template,
    aiTemplateEnabled,
    canRebuildDraft,
    buildFreshDraft,
  ]);
  editorRef.current = editor;

  // Task 9: hand the editor to the parent (CaseViewer) for the stone review
  // surface's report write-back. Fire on mount and clear (`null`) on unmount so
  // the parent's useStoneReportSync re-runs against a live editor and never a
  // destroyed one.
  useEffect(() => {
    onEditorReady?.(editor);
    return () => onEditorReady?.(null);
  }, [editor, onEditorReady]);

  // Auto-fold recovery for a phantom "empty" draft. A saved draft that is the
  // untouched negative template was almost certainly auto-created before this
  // study's AI findings existed (opened pre-inference: the mount auto-impression
  // wrote a "normal" impression from the template defaults and autosave
  // persisted it). The findings arrive later but the phantom draft is loaded
  // verbatim and shadows them — a safety issue (findings silently hidden).
  //
  // Detect that exact case and rebuild from the findings so they can't stay
  // hidden. `isUnworkedTemplateDraft` is strict (findings signature must equal a
  // fresh template), so we NEVER overwrite real rad work — a folded finding,
  // rad-added chip, or edited default line all disqualify the draft, leaving it
  // untouched (manual "Reset draft" still available). setContent emits an update
  // so the existing autosave heals the stored draft; next open sees findings in
  // the draft and this won't re-fire. Fresh studies (no saved draft) already
  // fold correctly via the outer gate, so we require a saved draft here.
  const autoFoldedFindingsRef = useRef(false);
  useEffect(() => {
    if (autoFoldedFindingsRef.current) return;
    if (!editor || initialSigned) return;
    if (reporterAiMode !== "inline") return; // only the inline arm folds findings into the doc
    if (!template || !reportData) return; // need both to build and to know findings are ready
    if (!canRebuildDraft) return;
    const savedState = reportState?.structured_editor_state;
    if (!savedState) return; // no shadowing draft — fresh path already folded findings
    const confidentCount = Object.values(sectionsForDoc).reduce(
      (n, section) => n + (section?.findings?.length ?? 0),
      0,
    );
    if (confidentCount === 0) return; // nothing to recover (genuinely no confident findings)
    if (!isUnworkedTemplateDraft(savedState, template)) return; // never clobber real work
    autoFoldedFindingsRef.current = true;
    editor.commands.setContent(buildFreshDraft());
    onReportContentApplied?.();
    streamImpressionRef.current?.(); // regenerate the impression from the real findings
    emit("toast", {
      message: `Loaded ${confidentCount} AI finding${confidentCount === 1 ? "" : "s"} into this report`,
    });
  }, [
    editor,
    initialSigned,
    reporterAiMode,
    template,
    reportData,
    reportState,
    canRebuildDraft,
    buildFreshDraft,
  ]);

  // rdn60 stone-overlay demo: append the saved stone's longest dimension to the
  // kidneys-ureters stone finding's chip text. Decoupled from the one-shot
  // build effect above (the labeling annotation fetches in parallel, so the
  // measurement can land after the doc is built); the `wasEmpty` guard above
  // means a later build pass won't overwrite the injected text. Retries briefly
  // until the chip is folded into the doc. No-op unless an overlay measurement
  // for THIS study is present (set by `useSavedMaskOverlay`).
  const stoneMeasurement = useAtomValue(stoneOverlayMeasurementAtom);
  useEffect(() => {
    if (!editor || !reportData || !stoneMeasurement) return;
    if (stoneMeasurement.studyUid !== (studyId ?? "").split("::")[0]) return;
    let stoneFinding: { id: string; text: string; condition: string } | undefined;
    for (const sec of Object.values(reportData.sections ?? {})) {
      const f = sec.findings?.find((mf) => /stone|ureteral|calcul/i.test(mf.condition ?? ""));
      if (f) {
        stoneFinding = f;
        break;
      }
    }
    if (!stoneFinding) {
      logger.warn(
        `[StoneOverlay] no stone finding in report for ${stoneMeasurement.studyUid} — measurement not injected`,
      );
      return;
    }
    if ((stoneFinding.text ?? "").includes("Longest dimension")) return;
    const finding = stoneFinding;
    const newText = `${(finding.text ?? "").trimEnd()} Longest dimension: ${stoneMeasurement.longAxisMm.toFixed(1)} mm.`;
    const inject = () => {
      const ok = editor.commands.replaceChipText(finding.id, newText);
      if (ok) {
        logger.info(
          `[StoneOverlay] injected longest dimension (${stoneMeasurement.longAxisMm.toFixed(1)} mm) into ${finding.condition}`,
        );
      }
      return ok;
    };
    if (inject()) return;
    // Chip not folded into the doc yet — retry briefly until it is.
    let attempts = 0;
    const iv = setInterval(() => {
      attempts++;
      if (inject() || attempts >= 20) {
        if (attempts >= 20) {
          logger.warn(
            `[StoneOverlay] stone chip ${finding.id} never appeared — measurement not injected`,
          );
        }
        clearInterval(iv);
      }
    }, 300);
    return () => clearInterval(iv);
  }, [editor, reportData, stoneMeasurement, studyId]);

  // Initial-doc snapshot — fires once per studyId after the editor has a
  // populated doc (either via mount-time `initialContent` or the recovery
  // setContent above). Re-arms on studyId transition so each case captures
  // its own baseline. Skipped for already-signed reports because the
  // "initial" semantics no longer apply (the saved doc is post-edit).
  const initialDocFiredForRef = useRef<string | null>(null);
  useEffect(() => {
    initialDocFiredForRef.current = null;
  }, [studyId]);
  useEffect(() => {
    if (!onInitialDoc) return;
    if (!editor || initialSigned) return;
    if (initialDocFiredForRef.current === studyId) return;
    // Empty doc means template hasn't populated yet — wait for recovery.
    if (editor.state.doc.content.size <= 2) return;
    initialDocFiredForRef.current = studyId;
    if (!template?.id) {
      // Audit log expects a stable template_id — log instead of silently
      // recording "unknown" so missing-template wiring is debuggable.
      console.warn("[study] initial doc fired without template metadata");
    }
    onInitialDoc({
      reportHtml: editor.getHTML(),
      templateId: template?.id ?? "unknown",
      templateCharCount: getTemplateCharCount(template),
    });
  }, [editor, studyId, initialSigned, template, reportData, onInitialDoc]);

  // Wire the FieldNode storage so ChipView's inline ↗ icon and the
  // InlineChipCard's "jump" button can call out to the case viewer.
  useEffect(() => {
    if (!editor) return;
    const storage = editor.storage.field as
      | { onJump?: (target: FindingJumpTarget) => void }
      | undefined;
    if (!storage) return;
    storage.onJump = (target) => onFindingClickRef.current?.(target);
  }, [editor]);

  // Wire the regenerate-impression widget (at the IMPRESSION header) to the
  // LLM stream, and keep its spinner/emphasis in sync with React state. The
  // widget reads editor.storage; mutating storage doesn't recompute the
  // decoration, so dispatch a meta-only transaction to refresh it.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const storage = editor.storage.regenerateImpression;
    storage.onRegenerate = () => streamImpressionRef.current?.();
    storage.streaming = impressionStatus === "streaming";
    editor.view.dispatch(editor.state.tr.setMeta(regenerateImpressionRefreshMeta, true));
  }, [editor, impressionStatus]);

  // AI sync is one-shot: buildReportDoc folds reportData.sections AND
  // reportData.toConsider into the initial HTML when the editor first
  // populates. Once loaded, the PM doc IS the source of truth — deletions
  // are permanent, no re-injection. See the cold-mount effect below for the
  // single AI→doc seed pass.

  // === Chip action helpers ================================================
  // Pure DOM-element-keyed operations on a chip. Each accepts a chip's
  // `.report-field` element and dispatches the corresponding mutation
  // against the live editor. Shared by the right-click context menu, the
  // focused-chip keyboard handler, and the click-to-jump side-effect.
  const dismissChip = useCallback(
    (fieldSpan: Element) => {
      if (!editor || !editor.isEditable) return;
      // Resolve the field by its id and delete the whole node — same action
      // as the InlineChipCard "remove" button. (posAtDOM on the atom chip's
      // wrapper lands in the parent paragraph, not the field, so the old
      // parent-type guard silently no-op'd the right-click Dismiss.)
      const chipId = fieldSpan.getAttribute("data-id");
      if (!chipId) return;
      const hit = findFieldNodeById(editor.state.doc, chipId);
      if (!hit) return;
      const tr = editor.state.tr;
      let end = hit.pos + hit.node.nodeSize;
      // Swallow a trailing space separator so chip spacing stays clean.
      const after = tr.doc.textBetween(end, Math.min(end + 1, tr.doc.content.size));
      if (after === " ") end += 1;
      tr.delete(hit.pos, end);
      editor.view.dispatch(tr);
      // Refocus the editor after the menu action. The right-click menu
      // button stole DOM focus; without this, a follow-up mod+z finds
      // neither editor.isFocused nor a non-"none" reportFocusAtom and
      // (a) no-ops on /prototype-report or (b) fires the viewer's
      // annotation-undo in the labeling CaseViewer. focus() lands the
      // caret in prose, and the delete's selectionUpdate re-seeds
      // reportFocusAtom to caret-in-prose — so the editor's own mod+z
      // (PM history) and the viewer's reportUndoWins gate both route to
      // the report. No dismissal bookkeeping: saved editor state is the
      // source of truth, so a deleted chip stays deleted across reloads.
      editor.commands.focus();
    },
    [editor],
  );

  const jumpForChip = useCallback((source: Element | FindingJumpTarget) => {
    const target = source instanceof Element ? jumpTargetFromChipDom(source) : source;
    if (target) onFindingClickRef.current?.(target);
  }, []);

  // "Add finding" from a chip's right-click menu: append an empty chip to the
  // chip's organ subsection and focus it. Mirrors the inline "+ Add finding"
  // widget via the shared insertRadFinding helper.
  const handleAddFinding = useCallback(
    (fieldSpan: Element) => {
      if (!editor || !editor.isEditable) return;
      const kind = fieldSpan.closest(".organ-subsection")?.getAttribute("data-kind");
      if (!kind) return;
      // Position just inside the close of the section's first paragraph.
      const paraEnds: number[] = [];
      editor.state.doc.descendants((node, pos) => {
        if (paraEnds.length) return false;
        if (node.type.name === "organSubsection" && node.attrs.kind === kind) {
          node.forEach((child, off) => {
            if (paraEnds.length || child.type.name !== "paragraph") return;
            paraEnds.push(pos + 1 + off + child.nodeSize - 1);
          });
          return false;
        }
        return true;
      });
      const paraInnerEnd = paraEnds[0];
      if (paraInnerEnd !== undefined) insertRadFinding(editor.view, paraInnerEnd);
    },
    [editor],
  );

  // Chip capability predicates. The right-click menu items and the E/M
  // shortcuts must agree with what the downstream flow can actually render
  // (issue #703: the menu gated on `data-candidate-attrs` alone while the
  // card also required condition/phrasing, so "Edit attributes" on a chip
  // from an older saved draft opened nothing, silently). Deriving both
  // booleans from the SAME gate functions the action callbacks run makes
  // that agreement structural: the menu offers exactly what the action can
  // do. canEditAttrs also mirrors InlineChipCard's render gate exactly —
  // same parseChipPhraseSources parse, same raw condition attr (the shared
  // canRebuildPhrase predicate treats an empty slug as no-condition), and
  // attrs-only because the card has no reportData to text-match against.
  // canMeasure adds resolveChipCondition's legacy text-match fallback.
  const chipEditability = useCallback(
    (chipId: string): { canEditAttrs: boolean; canMeasure: boolean } => {
      if (!editor) return { canEditAttrs: false, canMeasure: false };
      return {
        canEditAttrs: gateEditAttrsForChip(editor.state.doc, chipId).ok,
        canMeasure: gateMeasureForChip(editor.state.doc, chipId, reportData?.sections).ok,
      };
    },
    [editor, reportData],
  );

  // "Edit attributes" is reached two ways — the chip right-click menu and
  // the E key on a focused chip — and both funnel here. This is the SINGLE
  // entry point that opens the InlineChipCard: focus the chip's inner span
  // (sets the focused-chip SSoT) AND arm the card's render gate
  // (InlineChipCard renders only when info.id === inlineCardOpenChipIdAtom).
  // No modifier-click opener — a plain click just places the caret.
  const openEditAttrsForChip = useCallback(
    (fieldSpan: Element) => {
      if (!editor) return;
      const chipId = fieldSpan.getAttribute("data-id");
      if (!chipId) return;
      // Belt for entry paths that don't pre-gate (E shortcut, stale menus):
      // opening the card on a chip without pills would render nothing.
      const gate = gateEditAttrsForChip(editor.state.doc, chipId);
      if (!gate.ok) {
        logger.warn("%s id=%s", gate.warnId, chipId);
        emit("toast", { message: CHIP_GATE_TOASTS[gate.warnId] });
        return;
      }
      const inner = fieldSpan.querySelector(`.${CHIP_INNER_CLASS}`) as HTMLElement | null;
      inner?.focus();
      jotaiStore.set(inlineCardOpenChipIdAtom, chipId);
    },
    [editor],
  );

  const enterMeasureForChip = useCallback(
    (fieldSpan: Element) => {
      if (!editor) return;
      const chipId = fieldSpan.getAttribute("data-id");
      if (!chipId) return;
      // gateMeasureForChip resolves the field by id (never posAtDOM — the
      // field is an atom node, so DOM positions land in the parent
      // paragraph), checks the phrase sources, and resolves the condition
      // from the node attr with the legacy text-match fallback — the old
      // text-match-only lookup broke the moment the rad edited the chip's
      // text, silently no-oping on exactly the findings being worked on.
      // Each refusal branch toasts + warns with its own stable id.
      const gate = gateMeasureForChip(editor.state.doc, chipId, reportData?.sections);
      if (!gate.ok) {
        logger.warn("%s id=%s", gate.warnId, chipId);
        emit("toast", { message: CHIP_GATE_TOASTS[gate.warnId] });
        return;
      }
      const { fieldStart, fieldEnd, condition, phrasing, candidates } = gate;
      const baseName = humanizeBaseFromCondition(condition);
      // Phrasing path seeds from the engine's gated picks; the candidates
      // fallback seeds from the prob-thresholded default. joinPhrasing /
      // rebuildPhrase map this selection onto the right dims downstream.
      const selected = phrasing
        ? phrasingDefaultSelection(phrasing)
        : defaultAttributeSelection(candidates);
      const findingId = `${fieldStart}::${condition}`;
      const cohortJobId = (studyId ?? "").split("::")[1] ?? "";
      runCommand("enterMeasureMode", {
        request: {
          findingId,
          jobId: cohortJobId,
          fieldStart,
          fieldEnd,
          condition,
          baseName,
          selected,
          candidates,
          phrasing,
        },
      });
    },
    [editor, reportData, studyId],
  );

  // Find a chip's DOM element by its data-id. Used by the right-click menu
  // (state stores the id, action callback looks up the live element) and the
  // focused-chip keyboard handler.
  const findChipById = useCallback(
    (chipId: string): HTMLElement | null => {
      if (!editor?.view?.dom) return null;
      return editor.view.dom.querySelector(`.report-field[data-id="${CSS.escape(chipId)}"]`);
    },
    [editor],
  );

  // === Click + right-click ================================================
  // Left-click on a .has-link chip fires the slice jump as a side-effect
  // (the caret also places for inline text editing). Right-click opens the
  // context menu — the single home for every chip verb (jump, edit
  // attributes, measure, dismiss). No modifier-click gestures.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Auto-jump: clicking anywhere on a linked chip fires the slice jump
      // in addition to placing the caret in the inner contenteditable. The
      // explicit ↗ icon is the discoverable affordance; clicking the chip
      // body still jumps (preserves the previous behavior radiologists
      // expect — sliding the viewer along as they navigate the report).
      const linkedField = target.closest(".report-field.has-link") as HTMLElement | null;
      if (linkedField) {
        jumpForChip(linkedField);
        // Don't preventDefault — the inner contenteditable still needs to
        // receive the click so the caret lands for inline editing.
      }
    };

    const handleContextMenu = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Organ-subsection labels are handled by useSectionContextMenu (its own
      // contextmenu listener). Here we handle chips, then the impression menu.

      // Chip → chip actions menu. (A chip inside an impression item wins here,
      // before the impression menu below.)
      const chip = target.closest(".report-field") as HTMLElement | null;
      if (chip) {
        event.preventDefault();
        event.stopPropagation();
        const chipId = chip.getAttribute("data-id");
        if (!chipId) return;
        const { canEditAttrs, canMeasure } = chipEditability(chipId);
        const inImpression = !!chip.closest('section[data-section-kind="impression"]');
        const inOrganSubsection = !!chip.closest(".organ-subsection");
        const condition = chip.getAttribute("data-condition");
        setChipContextMenu({
          chipId,
          anchor: { x: event.clientX, y: event.clientY },
          canEditAttrs,
          canMeasure,
          // Any chip is dismissable (including normal/template placeholders),
          // matching the ⌫ keyboard behavior.
          canDismiss: editor.isEditable,
          canAddFinding: inOrganSubsection && editor.isEditable,
          canRegenerate: inImpression && editor.isEditable,
          // Reviewer: only findings with a condition are judgeable (no silent guess).
          canVerdict: !!reviewVerdict && !!condition && inOrganSubsection,
          condition: condition ?? null,
        });
        return;
      }

      // IMPRESSION header (the ::before label targets the section element) or
      // any impression item → "Regenerate impression" menu.
      if (editor.isEditable && target.closest('section[data-section-kind="impression"]')) {
        event.preventDefault();
        event.stopPropagation();
        setImpressionContextMenu({ anchor: { x: event.clientX, y: event.clientY } });
      }
    };

    dom.addEventListener("click", handleClick, { capture: true });
    dom.addEventListener("contextmenu", handleContextMenu);
    return () => {
      dom.removeEventListener("click", handleClick, { capture: true });
      dom.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [editor, jumpForChip, chipEditability, reviewVerdict]);

  // === Focused-chip sync (selection → atom + ref) =========================
  // reportFocusAtom writers driven by PM state. Two paths feed the SSoT
  // from this file (a third writer lives on ChipView's inner-span onFocus,
  // for the click-into-chip case that PM's selection model doesn't fire on
  // because FieldNode is atom: true):
  //
  //   selectionUpdate:
  //     - NodeSelection on a field    → chip-focused (Tab nav landed here)
  //     - TextSelection spans a field → chip-focused (legacy Tab path)
  //     - any other PM selection      → caret-in-prose at sel.from
  //
  //   focusout-from-editor → none (rad clicked into a button, viewer, etc.)
  //
  // selectionUpdate writes are guarded so they don't downgrade a fresh
  // ChipView caret-in-chip write for the same chip into caret-in-prose,
  // and don't downgrade ChipView's caret-in-chip into chip-focused unless
  // PM truly has NodeSelection on the field (Tab path).
  useEffect(() => {
    if (!editor) return;
    const update = () => {
      const sel = editor.state.selection;
      const doc = editor.state.doc;
      let next: ReportFocus;
      const maybeNode = (sel as { node?: { type: { name: string }; attrs: { id?: string } } }).node;
      if (maybeNode?.type.name === "field" && maybeNode.attrs.id) {
        next = {
          mode: "chip-focused",
          chipId: maybeNode.attrs.id,
          sectionKind: resolveSectionKindAt(doc, sel.from),
        };
      } else {
        const nodeAt = doc.nodeAt(sel.from);
        if (
          nodeAt?.type.name === "field" &&
          sel.to === sel.from + nodeAt.nodeSize &&
          typeof nodeAt.attrs.id === "string" &&
          nodeAt.attrs.id
        ) {
          next = {
            mode: "chip-focused",
            chipId: nodeAt.attrs.id,
            sectionKind: resolveSectionKindAt(doc, sel.from),
          };
        } else {
          next = {
            mode: "caret-in-prose",
            pos: sel.from,
            sectionKind: resolveSectionKindAt(doc, sel.from),
          };
        }
      }
      // Don't downgrade an active caret-in-chip write from ChipView (the
      // rad clicked into the chip inner span — PM selection lags behind
      // the DOM caret on atomic-field clicks, so PM would report
      // caret-in-prose at the chip's outer position one tick later).
      const current = getDefaultStore().get(reportFocusAtom);
      if (
        current.mode === "caret-in-chip" &&
        next.mode === "caret-in-prose" &&
        doc.nodeAt(sel.from)?.type.name === "field"
      ) {
        return;
      }
      setReportFocus(next);
    };
    const mapDictationPosition = ({ transaction }: { transaction: Transaction }) => {
      const target = editorDictationTargetRef.current;
      if (!transaction.docChanged || !target || target.pos === null) return;
      const mapped = transaction.mapping.mapResult(target.pos);
      target.pos = mapped.deletedAcross ? null : mapped.pos;
    };
    editor.on("selectionUpdate", update);
    editor.on("transaction", mapDictationPosition);
    update();
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", mapDictationPosition);
    };
  }, [editor, setReportFocus]);

  // Focus left the editor DOM entirely (toolbar button, viewer click,
  // browser-Tab to another window). Defer to a setTimeout so re-focus
  // inside the editor (chip → InlineChipCard → chip) doesn't transiently
  // null the atom and tear down the card.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    const handleFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Element | null;
      if (next?.closest?.(".rj-inline-card")) return;
      if (next?.closest?.(".report-field")) return;
      window.setTimeout(() => {
        const active = document.activeElement as Element | null;
        if (active?.closest?.(".rj-inline-card")) return;
        if (active?.closest?.(".report-field")) return;
        if (dom.contains(active)) return;
        setReportFocus({ mode: "none" });
      }, 0);
    };
    dom.addEventListener("focusout", handleFocusOut);
    return () => {
      dom.removeEventListener("focusout", handleFocusOut);
    };
  }, [editor, setReportFocus]);

  // === Tab gatekeeper ======================================================
  // Tab is universal chip navigation — regardless of where DOM focus lives,
  // Tab advances to the next chip in the report. Matches the rad's
  // workflow: mouse-wheel through the viewer to read images, hit Tab
  // (often bound to a mouse button) to step to the next finding, dictate,
  // scroll again, Tab again. Forcing them to click into the editor between
  // Tab presses is friction multiplied by every chip in every study.
  //
  // Branches:
  //   - target inside editor or strip card → let inner Tab handlers run
  //     (marks.ts navigate, SuggestionStrip onInputKeyDown).
  //   - target is a form input (search box, settings drawer) → preserve
  //     native Tab so the rad can move between fields in that surface.
  //   - everything else (viewer, toolbar, sidebar, body, popover) → route
  //     to nextField / prevField. marks.ts navigate handles the "no chip
  //     currently focused" case by falling back to PM selection (lands
  //     on first chip from `none` mode, last from Shift+Tab).
  //
  // Closes #404 — Tab cycle previously leaked into toolbar buttons and
  // broke after popover clicks. Now toolbar/sidebar buttons are skipped
  // entirely; Tab anywhere outside form inputs is chip nav.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const editorDom = editor.view.dom;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const target = e.target as Node | null;
      if (!target) return;
      if (editorDom.contains(target)) return;
      if (target instanceof Element && target.closest(".suggestion-strip__card")) return;
      const el = target instanceof Element ? target : null;
      if (
        el?.tagName === "INPUT" ||
        el?.tagName === "TEXTAREA" ||
        el?.tagName === "SELECT" ||
        el?.getAttribute("contenteditable") === "true"
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) editor.commands.prevField();
      else editor.commands.nextField();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [editor]);

  // === Focused-chip → DOM data attribute ==================================
  // CSS targets `.report-field[data-chip-focused="true"]` for the outline.
  // Also fires the slice-jump for linked chips so Tab, plain click, and any
  // card interaction all land on the chip's slice — `focusedChipId` is the
  // single source of truth for "this chip is the active one", same atom that
  // mounts InlineChipCard, so jumping here covers every entry path.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    dom
      .querySelectorAll('.report-field[data-chip-focused="true"]')
      .forEach((el) => el.removeAttribute("data-chip-focused"));
    if (focusedChipId) {
      const el = dom.querySelector(`.report-field[data-id="${CSS.escape(focusedChipId)}"]`);
      if (el) {
        el.setAttribute("data-chip-focused", "true");
        jumpForChip(el);
      }
    }
  }, [editor, focusedChipId, jumpForChip]);

  // === Focused-chip keyboard shortcuts ====================================
  // Always-attached keydown listener; reads the current focus from a ref so
  // there's no re-render race window between Tab firing and the handler
  // becoming active. Bare E/M/Backspace/Delete on a focused chip fire the
  // corresponding action; preventDefault unconditionally to swallow the
  // keystroke (otherwise the letter would type into the chip's selected
  // contents). No-op if the chip lacks the capability for that action.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    const handler = (event: KeyboardEvent) => {
      // Heading focus can leave a field selected in ProseMirror. Its keys
      // belong to the heading button, never to that previously selected chip.
      if (event.target instanceof Element && event.target.closest(".organ-subsection__label"))
        return;
      // Jotai's default store is synchronous — read the SSoT here instead
      // of mirroring it into a useRef. Project chip-focused / caret-in-chip
      // down to a chipId; other modes return null and skip the handler.
      const focusedId = getFocusedChipId(jotaiStore.get(reportFocusAtom));
      if (!focusedId) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Edit mode gate: if the rad's DOM focus is in the chip's inner
      // contentEditable span, they're typing — every key (Backspace, E, M,
      // letters) goes to the browser-native text editor, NOT this
      // navigation-mode handler. Without this gate, every Backspace inside
      // a Tab-navigated chip dismisses the whole chip instead of deleting
      // a character.
      const active = document.activeElement;
      if (active instanceof Element && active.classList.contains(CHIP_INNER_CLASS)) return;
      // Keyboard shortcuts only fire in "navigation focus" — the chip is
      // fully selected by Tab nav. When the rad is editing inside the chip
      // (cursor collapsed in the middle of text), let every letter through.
      const sel = editor.state.selection;
      const nodeAtSel = editor.state.doc.nodeAt(sel.from);
      const isFullChipSelection =
        nodeAtSel?.type.name === "field" && sel.to === sel.from + nodeAtSel.nodeSize;
      if (!isFullChipSelection) return;
      const chip = findChipById(focusedId);
      if (!chip) return;
      const k = event.key.toLowerCase();

      if (k === "e" || k === "m") {
        event.preventDefault();
        event.stopPropagation();
        // No capability pre-gate here: openEditAttrsForChip and
        // enterMeasureForChip each toast when the chip can't support the
        // action — the old data-candidate-attrs check silently swallowed
        // the keystroke instead.
        if (k === "e") openEditAttrsForChip(chip);
        else enterMeasureForChip(chip);
      } else if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault();
        event.stopPropagation();
        // reporter-jobs: dismiss any focused chip on ⌫/Delete, including
        // negative-finding (template) placeholders. Previously gated to
        // ai-finding only, which made the keystroke silently swallow on
        // "Normal liver." / "No biliary dilatation." chips.
        dismissChip(chip);
      }
    };
    dom.addEventListener("keydown", handler, { capture: true });
    return () => {
      dom.removeEventListener("keydown", handler, { capture: true });
    };
  }, [editor, findChipById, openEditAttrsForChip, enterMeasureForChip, dismissChip]);

  // Dispatcher for the chip context menu — looks up the chip by id (DOM may
  // have re-rendered between menu open and action) and runs the corresponding
  // helper. Returns void; menu closes itself.
  const handleChipMenuAction = useCallback(
    (action: ChipAction) => {
      if (!chipContextMenu) return;
      const chip = findChipById(chipContextMenu.chipId);
      if (!chip) return;
      if (action === "edit-attrs") openEditAttrsForChip(chip);
      else if (action === "measure") enterMeasureForChip(chip);
      else if (action === "add-finding") handleAddFinding(chip);
      else if (action === "dismiss") dismissChip(chip);
      else if (action === "regenerate") streamImpressionRef.current?.();
    },
    [
      chipContextMenu,
      findChipById,
      openEditAttrsForChip,
      enterMeasureForChip,
      handleAddFinding,
      dismissChip,
    ],
  );

  // Navigation keys leak from the editor into the viewer's slice stepper:
  // pressing End drove the axial series from slice 1/91 to 90/91, Enter
  // scrolled by ~26 slices, etc. Whichever handler is consuming them
  // (Cornerstone, a tool with built-in keyboard nav, or anything listening
  // on window/document) doesn't gate on focus — and it shouldn't, that's
  // the editor's responsibility. Stop these keys from propagating past the
  // editor so the radiologist can edit text without losing slice position.
  useEffect(() => {
    if (!editor?.view?.dom) return;
    const dom = editor.view.dom;
    const navKeys = new Set([
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "End",
      "Home",
      "PageUp",
      "PageDown",
      "Enter",
    ]);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (navKeys.has(event.key)) event.stopPropagation();
    };
    dom.addEventListener("keydown", handleKeyDown);
    return () => dom.removeEventListener("keydown", handleKeyDown);
  }, [editor]);

  // streamImpressionRef is read by the mount-only effect below (captures the
  // latest streamImpression without re-firing on callback identity changes).
  const streamImpressionRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const streamingAbortRef = useRef<AbortController | null>(null);

  // Ctrl/Cmd+E — focus the editor from anywhere.
  useHotkeys(
    "mod+e",
    () => editor?.commands.focus(),
    { preventDefault: true, enableOnContentEditable: true, enableOnFormTags: true },
    [editor],
  );

  // Stream an initial impression once the editor mounts. A saved draft is left
  // untouched unless it contains the retired canned-Impression marker, which
  // is replaced from its exact displayed Findings. Mount-only: parent remounts
  // via key={studyId}.
  useEffect(() => {
    if (!editor || !reportData || initialSigned) return;
    const savedState = reportState?.structured_editor_state;
    if (savedState && !hasLegacyTemplateImpression(savedState)) return;
    streamImpressionRef.current?.();
    return () => {
      streamingAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const extractFindingsForImpression = useCallback(
    () => (editor ? extractFindingsFromReportDoc(editor.getJSON()) : []),
    [editor],
  );

  // Apply a measurement result from CaseViewer's click-capture flow.
  // Re-finds the field by stored fieldStart/fieldEnd and rewrites the phrase
  // with the measurement appended. Slice link re-anchors to the click point.
  useEffect(() => {
    if (!editor || !measureResult || !measureMode) return;
    if (measureResult.findingId !== measureMode.findingId) return;

    const { fieldStart, fieldEnd, baseName, selected, candidates, phrasing } = measureMode;
    const node = editor.state.doc.nodeAt(fieldStart);
    if (!node || node.type.name !== "field") {
      runCommand("exitMeasureMode", {});
      setMeasureResult(null);
      return;
    }
    const expectedEnd = fieldStart + node.nodeSize;
    if (expectedEnd !== fieldEnd && Math.abs(expectedEnd - fieldEnd) > 200) {
      runCommand("exitMeasureMode", {});
      setMeasureResult(null);
      return;
    }
    // Engine phrasing joins pre-rendered tokens (+ measurement suffix); the
    // candidates path re-derives via rebuildPhrase for un-migrated cohorts.
    const measurement = { axesCm: measureResult.axesCm, volumeMl: measureResult.volumeMl };
    const newPhrase = phrasing
      ? joinPhrasingWithMeasurement(phrasing, selected, measurement)
      : rebuildPhrase(baseName, selected, candidates, measurement);
    const fieldType = editor.schema.nodes.field;
    const tr = editor.state.tr;
    tr.replaceWith(
      fieldStart,
      fieldStart + node.nodeSize,
      fieldType.create({
        ...node.attrs,
        seriesId: measureResult.seriesUid,
        sliceIndex: measureResult.sliceIndex,
        // Persist the measurement so a later pill toggle re-appends the suffix
        // (and suppresses the size descriptor) instead of dropping it when it
        // re-renders the phrase from `phrasing`, which has no measurement slot.
        measurement: JSON.stringify(measurement),
        text: newPhrase,
      }),
    );
    editor.view.dispatch(tr);

    // Keep measureMode active so shift/ctrl refinement clicks can continue.
    // Update fieldEnd to track the new phrase length — fieldStart stays put.
    const newFieldEnd = fieldStart + newPhrase.length;
    setMeasureMode((prev) => (prev ? { ...prev, fieldEnd: newFieldEnd } : null));
    setMeasureResult(null);
    setMeasureLoading(false);
  }, [editor, measureResult, measureMode, setMeasureMode, setMeasureResult, setMeasureLoading]);

  const streamImpression = useCallback(async () => {
    if (!editor) return;
    const findings = extractFindingsForImpression();
    if (findings.length === 0) return;
    const findingsAtStart = findReportSection(editor.state.doc, "findingsSection")?.node;
    if (!findingsAtStart) return;

    // Abort any in-flight impression stream to prevent interleaved output.
    streamingAbortRef.current?.abort();
    const abort = new AbortController();
    streamingAbortRef.current = abort;

    setImpressionStatus("streaming");

    const items: ImpressionItem[] = [];

    const renderImpression = (loading: boolean, completed = false) => {
      if (abort.signal.aborted) return;
      // PM JSON, not an HTML string \u2014 chips serialize through
      // FieldNode.renderHTML like every other report chip (see
      // buildImpressionSection / buildReportDoc).
      const sectionJson = buildImpressionSection(items, loading);
      const impression = findReportSection(editor.state.doc, "impressionSection");
      const currentFindings = findReportSection(editor.state.doc, "findingsSection")?.node;
      sectionJson.attrs = {
        ...impression?.node.attrs,
        findingsChanged: completed
          ? !currentFindings?.eq(findingsAtStart)
          : (impression?.node.attrs.findingsChanged ?? null),
      };

      if (impression !== null) {
        editor
          .chain()
          .command(({ tr }) => {
            // Programmatic rebuild — replacing N impression chips with the
            // streamed set is not a rad-initiated deletion.
            tr.setMeta(BULK_DELETE_TOAST_SKIP_META, true);
            return true;
          })
          .deleteRange({ from: impression.pos, to: impression.pos + impression.node.nodeSize })
          .insertContentAt(impression.pos, sectionJson, { updateSelection: false })
          .run();
      } else {
        // Defensive: if for some reason the impressionSection is missing,
        // append at the doc end so the rad still sees the items.
        editor.commands.insertContentAt(editor.state.doc.content.size, sectionJson, {
          updateSelection: false,
        });
      }
    };

    renderImpression(true);
    try {
      await requestImpression(findings, {
        signal: abort.signal,
        studyId,
        onItem: (item) => {
          items.push(item);
          renderImpression(false);
        },
      });
      renderImpression(false, true);
      if (abort.signal.aborted) return;
      setImpressionStatus("done");
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setImpressionStatus("error");
      showImpressionWarning(
        e instanceof Error ? `Impression failed: ${e.message}` : "Impression generation failed.",
      );
    }
  }, [editor, extractFindingsForImpression, showImpressionWarning, studyId]);
  streamImpressionRef.current = streamImpression;

  // Debounced autosave — persists structured_editor_state to DDB on every edit.
  // saveMutate identity is stable per TanStack Query v5.
  const saveMutate = saveReportState.mutate;

  useEffect(() => {
    if (!editor || !userId || !studyId || isSigned || reviewVerdict || readerContext?.isPractice)
      return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const onUpdate = () => {
      if (!autoSaveEnabled || pendingReaderSign.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (pendingReaderSign.current) return;
        setAutoSaveStatus("Saving...");
        if (readerSaveQueue) {
          void readerSaveQueue
            .save({
              structuredEditorState: editor.getJSON(),
              reportExamType: EXAM_TYPE ?? undefined,
            })
            .then(() => {
              if (!editor.isDestroyed && !pendingReaderSign.current) setAutoSaveStatus("Saved");
            })
            .catch((error: unknown) => {
              if (editor.isDestroyed || pendingReaderSign.current) return;
              setAutoSaveStatus(
                (error as { response?: { status?: number } })?.response?.status === 409
                  ? "Report changed elsewhere. Your edits are still here; copy them before reloading."
                  : "Save not confirmed. Your edits are still here; the next save will retry.",
              );
            });
          return;
        }
        saveMutate(
          {
            userId,
            studyId,
            structuredEditorState: editor.getJSON(),
            reportExamType: EXAM_TYPE ?? undefined,
          },
          {
            onSuccess: () => setAutoSaveStatus("Saved"),
            onError: (err: unknown) => {
              // Surface the actual reason — generic "Error saving" hides
              // browser-specific failures (Firefox CORS preflight, expired
              // token, missing axios timeout, etc.).
              let detail = "Error saving";
              if (err && typeof err === "object") {
                const e = err as {
                  response?: { status?: number };
                  message?: string;
                  code?: string;
                };
                if (e.response?.status) {
                  detail = `Save failed: HTTP ${e.response.status}`;
                } else if (e.code === "ERR_NETWORK") {
                  detail = "Save failed: network";
                } else if (e.message) {
                  detail = `Save failed: ${e.message}`;
                }
              }
              setAutoSaveStatus(detail);
              // eslint-disable-next-line no-console
              console.error("[PrototypeReport] save_report_state failed", err);
            },
          },
        );
      }, 1500);
    };

    editor.on("update", onUpdate);
    return () => {
      if (timer) clearTimeout(timer);
      editor.off("update", onUpdate);
    };
  }, [
    editor,
    userId,
    studyId,
    isSigned,
    reviewVerdict,
    autoSaveEnabled,
    saveMutate,
    readerSaveQueue,
    readerContext?.isPractice,
    setAutoSaveStatus,
    EXAM_TYPE,
  ]);

  // Bulk-delete visibility: a drag selection sweeping several chips + one
  // keystroke removes them silently (ReportStructureGuard allows it by
  // design). Toast with inline Undo so the accidental case is loud without
  // taxing the deliberate one.
  useEffect(() => {
    if (!editor) return;
    return attachBulkFieldDeleteToast(editor);
  }, [editor]);

  const handleSignReport = useCallback(async () => {
    if (!editor || !userId || !studyId) return;
    if (isSigned) return;
    if (readerSignInFlight.current) return;

    // Inline UX: AI suggestions live in the editor itself. What's in the
    // report at sign time IS the report. The legacy "pending suggestions"
    // gate (counting un-triaged toConsider items) no longer applies.
    let difficulty: number | undefined;
    let activeReadingMs: number | undefined;
    if (pendingReaderSign.current) {
      difficulty = pendingReaderSign.current.difficulty;
    } else if (confirmSign) {
      const result = await confirmSign();
      if (!result) return;
      difficulty = result.difficulty;
      activeReadingMs = result.activeReadingMs;
    } else if (!window.confirm("Sign this report? It will be locked from further edits.")) {
      return;
    }

    if (editor.isDestroyed || readerSignInFlight.current) return;
    const templateId = template?.id ?? "unknown";
    const templateCharCount = getTemplateCharCount(template);

    setAutoSaveStatus("Signing...");

    // Reader signing has one authority: the atomic final snapshot + event.
    // Keep this exact document frozen while an uncertain result is retried.
    if (readerContext) {
      if (!onSigned) {
        setAutoSaveStatus("Reader signing is unavailable. Reload to try again.");
        return;
      }
      const context = pendingReaderSign.current ?? {
        templateId,
        templateCharCount,
        difficulty,
        activeReadingMs,
      };
      pendingReaderSign.current = context;
      readerSignInFlight.current = true;
      setIsReaderSigning(true);
      editor.setEditable(false);
      try {
        if (readerSaveQueue && !context.expectedRevision) {
          context.expectedRevision = await readerSaveQueue.save({
            structuredEditorState: editor.getJSON(),
            reportExamType: EXAM_TYPE ?? undefined,
          });
        }
        if (editor.isDestroyed) return;
        await onSigned(context);
        if (editor.isDestroyed) return;
        setIsSigned(true);
        setAutoSaveStatus("Signed");
      } catch (err) {
        console.error("[sign] audit-trail sign failed", err);
        if (editor.isDestroyed) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setAutoSaveStatus(
          status === 409
            ? "Report changed elsewhere. Your edits are still here; copy them before reloading."
            : status === 422
              ? "Report was not signed. Copy your edits before reloading to revise it."
              : "Sign not confirmed. Click Sign Report to retry the same report.",
        );
      } finally {
        readerSignInFlight.current = false;
        if (!editor.isDestroyed) setIsReaderSigning(false);
      }
      return;
    }

    // Stamp the A/B arm + interaction counts onto the closing transaction so
    // downstream analysis can attribute time-to-sign / edit-distance / accept-
    // rate per arm. Sending undefined (BE drops the field) for arms where the
    // event doesn't apply keeps null=not-applicable distinct from zero.
    const isStrip = reporterAiMode === "strip";
    const finalReportHtml = editor.getHTML();
    const finalReportText = getReportText(editor);
    signReport.mutate(
      {
        userId,
        studyId,
        finalReportHtml,
        finalReportText,
        reporterAiMode,
        acceptedFindingsCount: isStrip ? acceptedFindingsCountRef.current : undefined,
        dismissedFindingsCount: isStrip ? dismissedFindingsCountRef.current : undefined,
        voiceTuneCount: voiceTuneCountRef.current,
      },
      {
        onSuccess: () => {
          editor.setEditable(false);
          setIsSigned(true);
          setAutoSaveStatus("Signed");
        },
        onError: () => setAutoSaveStatus("Sign failed"),
      },
    );
  }, [
    editor,
    userId,
    studyId,
    isSigned,
    reporterAiMode,
    signReport,
    setAutoSaveStatus,
    confirmSign,
    onSigned,
    readerContext,
    readerSaveQueue,
    EXAM_TYPE,
    template,
  ]);

  const handleClearDraft = useCallback(
    (force = false) => {
      if (!editor || isSigned) return;
      if (!canRebuildDraft) {
        emit("toast", {
          message: "A new AI draft is unavailable. Your edits have not been changed.",
        });
        return;
      }
      if (!force && !window.confirm("Reset to AI draft? Your edits will be lost.")) return;

      // buildReportDoc gates AI by A/B arm: inline folds in confident +
      // maybe; strip/off start from pure template defaults. For blank-
      // template projects (no reportData), the gate is moot — sections={}
      // produces just the template defaults regardless.
      const fresh = buildFreshDraft();

      editor
        .chain()
        .command(({ tr }) => {
          tr.setMeta(BULK_DELETE_TOAST_SKIP_META, true);
          return true;
        })
        .setContent(fresh)
        .run();
      onReportContentApplied?.();
      setShowNormalFields(true);
      setImpressionStatus("idle");
      setAutoSaveStatus("");

      // Re-stream the initial impression for fresh studies in the inline arm
      // only — strip/off don't have AI chips in the editor for the LLM to
      // summarize until the rad accepts cards (strip) or types findings (off).
      if (reportData && reporterAiMode === "inline") {
        streamImpressionRef.current?.();
      }
    },
    [
      editor,
      isSigned,
      reportData,
      reporterAiMode,
      setAutoSaveStatus,
      canRebuildDraft,
      buildFreshDraft,
    ],
  );

  // Auto-reset the editor when the A/B mode changes. Each mode has a
  // different starting state (inline = AI chips folded in; strip = template
  // only, AI waits in the strip; off = template only, no AI), so a mode
  // flip mid-session needs a fresh draft to make sense — otherwise the
  // editor keeps whatever was there from the previous mode and the rad has
  // to manually click Clear. Skip the first render so loading a saved draft
  // doesn't get wiped (the outer's isPrefsLoading gate guarantees we mount
  // with the persisted mode, not the atom default).
  //
  // Deferred to a microtask because handleClearDraft → editor.commands.
  // setContent calls flushSync inside Tiptap; running it directly during
  // the effect's render-commit window triggers React's "flushSync was
  // called from inside a lifecycle method" warning.
  const lastModeRef = useRef(reporterAiMode);
  useEffect(() => {
    if (lastModeRef.current === reporterAiMode) return;
    lastModeRef.current = reporterAiMode;
    if (!editor || isSigned) return;
    queueMicrotask(() => {
      if (editor.isDestroyed) return;
      handleClearDraft(true);
    });
  }, [reporterAiMode, editor, isSigned, handleClearDraft]);

  // Toggling review-tier findings only changes the inline editor after a
  // rebuild (buildReportDoc bakes findings in at build time — there is no
  // reconciler). Strip mode reflects the toggle live and off mode has no AI
  // in the editor, so only the inline arm needs action. Unlike the mode flip
  // above, offer a confirm rather than a silent forced reset so the rad
  // doesn't lose edits unawares; declining leaves the toggle set and applies
  // on the next manual reset. Skips the first render so loading a saved draft
  // under the persisted toggle doesn't prompt (atom uses getOnInit).
  const lastShowLowConfRef = useRef(showLowConfidenceFindings);
  useEffect(() => {
    if (lastShowLowConfRef.current === showLowConfidenceFindings) return;
    lastShowLowConfRef.current = showLowConfidenceFindings;
    if (reporterAiMode !== "inline" || !editor || isSigned) return;
    queueMicrotask(() => {
      if (editor.isDestroyed) return;
      if (window.confirm("Reset draft to apply the change? Your edits will be lost.")) {
        handleClearDraft(true);
      }
    });
  }, [showLowConfidenceFindings, reporterAiMode, editor, isSigned, handleClearDraft]);

  const handleUnsignReport = useCallback(() => {
    if (readerContext) return;
    if (!editor || !userId || !studyId) return;
    if (!isSigned) return;
    if (!window.confirm("Unsign this report? You'll be able to edit it again.")) return;

    setAutoSaveStatus("Unsigning...");
    unsignReport.mutate(
      { userId, studyId },
      {
        onSuccess: () => {
          editor.setEditable(true);
          setIsSigned(false);
          setAutoSaveStatus("");
        },
        onError: () => setAutoSaveStatus("Unsign failed"),
      },
    );
  }, [editor, userId, studyId, isSigned, unsignReport, setAutoSaveStatus, readerContext]);

  // Show / hide normal subsections. Subsections always exist in the doc;
  // hiding is purely visual via the editor wrapper's `editor-hide-normals`
  // class. CSS uses `:has()` to hide any organSubsection whose chips are
  // entirely template — once any chip provenance differs, the subsection
  // becomes visible again.
  const handleToggleNormalFields = useCallback(() => {
    setShowNormalFields((v) => !v);
  }, []);

  // Ctrl+N — toggle normal fields. Ctrl+G — regenerate impression.
  // Modifier-bound so a capital N / G typed (or dictated) into the editor
  // stays as text. CE / form-tag flags so the binding still fires while
  // the rad's keyboard focus is in the report editor.
  useHotkeys(
    "ctrl+n",
    () => handleToggleNormalFields(),
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [handleToggleNormalFields],
  );
  useHotkeys(
    "ctrl+g",
    () => streamImpression(),
    {
      preventDefault: true,
      enableOnContentEditable: true,
      enableOnFormTags: true,
    },
    [streamImpression],
  );

  // Cmd/Ctrl+Z (and Shift for redo) for the REPORT. The viewer's annotation
  // undo cedes mod+z when the report is active (useViewerHotkeys.reportUndoWins).
  // When the editor itself is focused, ProseMirror's history keymap handles it
  // — we bail to avoid a double-undo. When a chip's inner contenteditable is
  // focused (a NodeView outside PM's focus tracking, so editor.isFocused is
  // false), we drive the same undo/redo the toolbar buttons use.
  //
  // Arbitrate on focus, never mouse hover: hoveredSurfaceAtom's own doc says
  // modifier hotkeys must not depend on it. editor.isFocused covers the
  // PM-view caret; reportFocusAtom covers the chip inner contenteditable.
  const reportContextActive = useCallback(() => {
    if (!editor) return false;
    return editor.isFocused || jotaiStore.get(reportFocusAtom).mode !== "none";
  }, [editor]);
  useHotkeys(
    "mod+z",
    () => {
      if (!editor || editor.isFocused || !reportContextActive()) return;
      editor.chain().focus().undo().run();
    },
    { preventDefault: true, enableOnContentEditable: true, enableOnFormTags: true },
    [editor, reportContextActive],
  );
  useHotkeys(
    "mod+shift+z",
    () => {
      if (!editor || editor.isFocused || !reportContextActive()) return;
      editor.chain().focus().redo().run();
    },
    { preventDefault: true, enableOnContentEditable: true, enableOnFormTags: true },
    [editor, reportContextActive],
  );

  const reportContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      ref={reportContainerRef}
      className="reporter-jobs report-container"
      onMouseEnter={() => setHoveredSurface("editor")}
      onMouseLeave={() => setHoveredSurface((prev) => (prev === "editor" ? null : prev))}
    >
      {/* Stale-draft banner: prior-schema marks (bracket / insertedHighlight /
          findingLink) would crash the editor on mount. We skipped loading the
          draft above; surface a one-click recovery instead. */}
      {staleDraftDetected && !isSigned && (
        <div
          role="alert"
          className="bg-surface-2 border border-border-strong text-fg text-sm px-4 py-2 mb-2 rounded flex items-center justify-between gap-3"
        >
          <span>
            {templateMismatchDraftDetected
              ? "This saved draft was created with a different report template."
              : "This saved draft uses an outdated format and can't be loaded."}
          </span>
          <button
            onClick={() => handleClearDraft(true)}
            className="report-btn report-btn--ghost shrink-0"
          >
            Reset Draft
          </button>
        </div>
      )}
      {/* Dictation status lives on the control-bar Dictate button (the single
          dictation-status home), driven by dictationPhase below. Transient
          warnings fire on the page-level toast bus (PageToast). */}
      {/* AI suggestions placement depends on the A/B arm.
          - inline: folded into the initial doc by buildReportDoc.
          - strip:  triage list above the editor, accept promotes a card
                    into the doc as a chip.
          - off:    no AI anywhere — pure template. */}
      {reporterAiMode === "strip" && aiTemplateEnabled && template ? (
        <StripSplit
          top={
            <SuggestionStrip
              editor={editor}
              studyId={studyId}
              reportData={reportData}
              template={template}
              onAccept={() => {
                acceptedFindingsCountRef.current += 1;
              }}
              onDismiss={() => {
                dismissedFindingsCountRef.current += 1;
              }}
              onJump={(target) => onFindingClickRef.current?.(target)}
            />
          }
          bottom={
            <div className={`report-editor ${showNormalFields ? "" : "editor-hide-normals"}`}>
              <EditorContent editor={editor} />
            </div>
          }
        />
      ) : (
        <div className={`report-editor ${showNormalFields ? "" : "editor-hide-normals"}`}>
          <EditorContent editor={editor} />
        </div>
      )}

      {/* reporter-jobs: inline chip editor card. Renders when a focused
          AI chip is set (via Tab nav); anchored just below the chip. */}
      <InlineChipCard editor={editor} containerEl={reportContainerRef.current} />

      {/* Right-click context menu for chips — replaces the four inline buttons */}
      {chipContextMenu && (
        <ChipContextMenu
          key={chipContextMenu.chipId}
          anchor={chipContextMenu.anchor}
          canEditAttrs={chipContextMenu.canEditAttrs}
          canMeasure={chipContextMenu.canMeasure}
          canDismiss={chipContextMenu.canDismiss}
          canAddFinding={chipContextMenu.canAddFinding}
          canRegenerate={chipContextMenu.canRegenerate}
          canVerdict={chipContextMenu.canVerdict}
          currentVerdict={
            chipContextMenu.condition
              ? reviewerVerdicts[chipContextMenu.condition]?.verdict
              : undefined
          }
          onVerdict={(verdict, reason, reasonDetail) => {
            if (!chipContextMenu.condition) {
              console.warn("[reviewer] verdict click on chip without condition — ignored");
              return;
            }
            setReviewerVerdict(
              chipContextMenu.condition,
              verdict,
              reason ?? null,
              reasonDetail ?? null,
            );
          }}
          onAction={handleChipMenuAction}
          onClose={closeChipContextMenu}
        />
      )}

      {/* Right-click Rename / Remove on organ-subsection labels (shared hook) */}
      {sectionContextMenu}

      {/* Right-click context menu for the IMPRESSION header / items */}
      {impressionContextMenu && (
        <ImpressionContextMenu
          anchor={impressionContextMenu.anchor}
          disabled={impressionStatus === "streaming"}
          onRegenerate={() => streamImpressionRef.current?.()}
          onClose={closeImpressionContextMenu}
        />
      )}

      {!reviewVerdict && (
        <ReportControlBar
          canUndo={!!editor?.can().undo()}
          canRedo={!!editor?.can().redo()}
          onUndo={() => editor?.chain().focus().undo().run()}
          onRedo={() => editor?.chain().focus().redo().run()}
          isSigned={isSigned}
          autoSaveStatus={autoSaveStatus}
          hasReportData={!!reportData}
          dictationActive={!!(userId && token)}
          dictationPhase={dictationPhase}
          onToggleDictation={toggleEditorDictation}
          reporterAiMode={reporterAiMode}
          onChangeMode={setReporterAiMode}
          showNormalFields={showNormalFields}
          onToggleNormalFields={handleToggleNormalFields}
          onClearDraft={() => handleClearDraft()}
          onOpenMicSettings={() => setMicTestOpen(true)}
          canEditTemplates={canEditTemplates}
          sessionMode={sessionMode}
          onSignReport={handleSignReport}
          onUnsignReport={readerContext ? undefined : handleUnsignReport}
          editingLocked={pendingReaderSign.current !== null}
          isSigningPending={signReport.isPending || isReaderSigning}
          isUnsigningPending={unsignReport.isPending}
        />
      )}

      {/* Original radiologist report — lazy loaded on expand */}
      {studyId && !hideOriginalReport && <OriginalReportPanel studyId={studyId} />}
    </div>
  );
}
